export interface PricingContent {
  title: string;
  sections: Section[];
}

export interface Section {
  title: string;
  subsections: Subsection[];
}

export interface Subsection {
  title: string;
  items: ContentItem[];
}

export interface TableCell {
  content: string;
}

export interface TableRow {
  cells: TableCell[];
}

export interface ContentItem {
  number: string;
  content: string;
  subItems?: ContentItem[];
  table?: {
    headers?: string[];
    rows: TableRow[];
  };
}

export const normalPricingContent: PricingContent = {
  title: 'PRICING POLICY',
  sections: [
    {
      title: '1. Purpose',
      subsections: [
        {
          title: '1.1 Overview',
          items: [
            {
              number: '1.1.1',
              content:
                'This Pricing Policy (hereinafter referred to as "Policy") defines the pricing structure, feature access levels, and service usage conditions for the Spacemap Platform, including its key modules Astro-1, Conjunction Assessment (CA), and Collision Avoidance (COLA). The objective of this Policy is to establish a transparent and equitable service environment between the Company and its Users while ensuring compliance with applicable commercial and security regulations.',
            },
          ],
        },
        {
          title: '1.2 Legal Reference',
          items: [
            {
              number: '1.2.1',
              content:
                'This Policy constitutes Annex B to the Customer Agreement and forms an integral part of the Service Contract. In the event of any inconsistency between this Policy and the main Agreement, the provisions of the Agreement shall prevail.',
            },
          ],
        },
        {
          title: '1.3 Applicability',
          items: [
            {
              number: '1.3.1',
              content:
                'This Policy applies to all users of the Spacemap Platform, including both Basic and Paid subscribers (Standard, Advanced, and Enterprise plans).',
            },
          ],
        },
        {
          title: '1.4 Policy Maintenance',
          items: [
            {
              number: '1.4.1',
              content:
                'The Company reserves the right to amend this Policy in accordance with the Security & Compliance Policy (Chapter 10 – Documentation & Revision). Any updates shall be communicated to Users via the official Spacemap portal or registered email.',
            },
          ],
        },
      ],
    },
    {
      title: '2. Service Composition & Definitions',
      subsections: [
        {
          title: '2.1 Overview',
          items: [
            {
              number: '2.1.1',
              content:
                'This chapter defines the detailed functional structure and operational roles of the Spacemap Platform. The Platform integrates orbital data from satellites, launch vehicles, and space debris to perform Conjunction Assessment (CA) for collision prediction and Collision Avoidance (COLA) for maneuver simulation. It specifies the composition, data flow, and technical elements of Astro-1, the core module within the Platform.',
            },
          ],
        },
        {
          title: '2.2 Platform Overview',
          items: [
            {
              number: '2.2.1',
              content:
                'The Platform serves as the foundation of the Spacemap ecosystem, centered on Astro-1, which integrates the collection, analysis, and visualization of space-object data. Astro-1 is comprised of two primary sub-modules:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'CA (Conjunction Assessment): Analyzes close-approach events and collision risks between space objects.)',
                },
                {
                  number: 'b.',
                  content:
                    'COLA (Collision Avoidance): Simulates maneuver scenarios and optimizes collision avoidance solutions.',
                },
              ],
            },
          ],
        },
        {
          title: '2.3 Astro-1 Module',
          items: [
            {
              number: '2.3.1',
              content:
                'Astro-1 uses real-time orbital data to perform CA and COLA analysis. Each sub-module is composed of specialized functions as outlined below.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'The CA module analyzes the probability of close approaches between two space objects and quantifies collision risk. Through automated data collection and statistical modeling, it evaluates risk levels and visualizes the results for users.',
                  table: {
                    headers: ['Function Name', 'Description'],
                    rows: [
                      {
                        cells: [
                          {
                            content: 'Visualization',
                          },
                          {
                            content:
                              'Displays conjunction data and risk indices in 3D/2D visual formats.',
                          },
                        ],
                      },
                      {
                        cells: [
                          {
                            content: 'Search',
                          },
                          {
                            content:
                              'Retrieves CDM records by satellite name, time range, or risk criteria.',
                          },
                        ],
                      },
                      {
                        cells: [
                          {
                            content: 'Advanced Search',
                          },
                          {
                            content:
                              'Performs multi-criteria queries (e.g., multiple satellites, periods, or risk ranges).',
                          },
                        ],
                      },
                      {
                        cells: [
                          {
                            content: 'Historical CDM',
                          },
                          {
                            content:
                              'Provides a database for reviewing, comparing, and reanalyzing past conjunction events.',
                          },
                        ],
                      },
                    ],
                  },
                },
                {
                  number: 'b.',
                  content:
                    'The COLA module uses CA outputs as input data to simulate avoidance maneuvers (ΔV, RAAN adjustments, etc.) and quantitatively evaluate risk reduction performance. It compares multiple maneuver scenarios to recommend the most fuel-efficient and risk-optimized solution.',
                  table: {
                    headers: ['Function Name', 'Description'],
                    rows: [
                      {
                        cells: [
                          {
                            content: 'Avoidance Maneuver',
                          },
                          {
                            content:
                              'Calculates and proposes optimal orbit changes based on various collision-avoidance scenarios.',
                          },
                        ],
                      },
                    ],
                  },
                },
              ],
            },
          ],
        },
        {
          title: '2.4 Functional Integration',
          items: [
            {
              number: '2.4.1',
              content:
                'Results generated from the CA module are automatically transferred to the COLA module as simulation inputs. This integrated workflow allows users to detect collision risks in real time and immediately conduct avoidance simulations.',
            },
          ],
        },
        {
          title: '2.5 Technical Environment',
          items: [
            {
              number: '2.5.1',
              content: 'Data Formats: CDM (XML/JSON), TLE-based orbital data',
            },
            {
              number: '2.5.2',
              content:
                'Analytical Engine: SGP4 propagation and prediction algorithm',
            },
            {
              number: '2.5.3',
              content: 'Interfaces: RESTful API and WebGL-based visualization',
            },
            {
              number: '2.5.4',
              content: 'Authentication: OAuth 2.0 / API Key',
            },
            {
              number: '2.5.5',
              content: 'Operating Environment: Linux-based server',
            },
          ],
        },
        {
          title: '2.6 Version & Maintenance',
          items: [
            {
              number: '2.6.1',
              content:
                'CA and COLA functions are maintained on a version basis, and all updates or revisions are documented in accordance with Chapter 10 of the Security & Compliance Policy (Documentation and Revision Procedures). Major changes are announced in advance through the official Spacemap portal and client notification channels.',
            },
          ],
        },
      ],
    },
    {
      title: '3. Pricing Structure and Plan Descriptions',
      subsections: [
        {
          title: '3.1 Purpose',
          items: [
            {
              number: '3.1.1',
              content:
                'This chapter defines the pricing structure, service plans, and subscription conditions for the Spacemap Platform. Each plan is designed to meet the needs of various user groups, from individual users to institutional and defense clients, based on usage scale, data access requirements, and operational objectives.',
            },
          ],
        },
        {
          title: '3.2 Overview of Service',
          items: [
            {
              number: '3.2.1',
              content:
                'The Spacemap Platform offers subscription plans tailored to different user needs:',
              table: {
                headers: ['Plan', 'Price', 'Description'],
                rows: [
                  {
                    cells: [
                      {
                        content: 'Basic',
                      },
                      {
                        content: 'Free of charge',
                      },
                      {
                        content:
                          'Provides basic platform access and limited data viewing capabilities.',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: 'Standard',
                      },
                      {
                        content: 'USD 100/month Or 1,000/year',
                      },
                      {
                        content:
                          'Offers extended features suitable for individual professionals and startups.',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: 'Advanced*',
                      },
                      {
                        content: 'USD 1,000/month Or 10,000/year',
                      },
                      {
                        content:
                          'Ideal for teams requiring frequent API usage and workflow automation. Price may vary depending on Add-on and Scaling Model. Additional feature for number of satellites and COLA.',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: 'Enterprise',
                      },
                      {
                        content: 'Custom Pricing',
                      },
                      {
                        content:
                          'Tailored for institutional and defense-sector clients; includes dedicated support, SLA options, and security integration.',
                      },
                    ],
                  },
                ],
              },
              subItems: [
                {
                  number: 'a.',
                  content: '',
                  table: {
                    headers: [
                      'Number of Managed Sats',
                      'Monthly Price',
                      'Yearly Price',
                    ],
                    rows: [
                      {
                        cells: [
                          {
                            content: '1-2',
                          },
                          {
                            content: 'USD 1,000',
                          },
                          {
                            content: 'USD 10,000',
                          },
                        ],
                      },
                      {
                        cells: [
                          {
                            content: '3-5',
                          },
                          {
                            content: 'USD 2,000',
                          },
                          {
                            content: 'USD 20,000',
                          },
                        ],
                      },
                      {
                        cells: [
                          {
                            content: '6-10',
                          },
                          {
                            content: 'USD 3,000',
                          },
                          {
                            content: 'USD 30,000',
                          },
                        ],
                      },
                      {
                        cells: [
                          {
                            content: '10-20',
                          },
                          {
                            content: 'USD 4,000',
                          },
                          {
                            content: 'USD 40,000',
                          },
                        ],
                      },
                      {
                        cells: [
                          {
                            content: '20+',
                          },
                          {
                            content: 'Negotiable',
                          },
                          {
                            content: 'Negotiable',
                          },
                        ],
                      },
                    ],
                  },
                },
              ],
            },
            {
              number: '3.2.2',
              content: 'Features of the Spacemap Platform are as follows:',
              table: {
                headers: [
                  'Plan',
                  'Basic',
                  'Standard',
                  'Advanced',
                  'Enterprise',
                ],
                rows: [
                  {
                    cells: [
                      {
                        content: 'Feature',
                      },
                      {
                        content: 'Visualization',
                      },
                      {
                        content: 'All in Basic',
                      },
                      {
                        content: 'All in Standard',
                      },
                      {
                        content: 'For Enterprise',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: 'CDM Basic',
                      },
                      {
                        content: 'CDM Unlimited for future',
                      },
                      {
                        content: 'CDM Archival (Max 3 Months)',
                      },
                      {
                        content: 'For Government',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: 'SpaceBot Basic',
                      },
                      {
                        content: '42Talk Unlimited',
                      },
                      {
                        content: 'MySTM (Max 10)',
                      },
                      {
                        content: 'For Education',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: '42Talk Basic',
                      },
                      {
                        content: 'MySTM (Max 3)',
                      },
                      {
                        content: 'COLA (Max 30 maneuvers for each sat / day)',
                      },
                      {
                        content: '',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: 'SpaceTube Public',
                      },
                      {
                        content: '',
                      },
                      {
                        content: 'SpaceTube Private',
                      },
                      {
                        content: '',
                      },
                    ],
                  },
                ],
              },
            },
          ],
        },
        {
          title: '3.3 Pricing and License Conditions',
          items: [
            {
              number: '3.3.1',
              content:
                'All prices are based on a monthly subscription model, billed in USD.',
            },
            {
              number: '3.3.2',
              content:
                'Advanced and Enterprise plans support multiple users under a consolidated organizational license.',
            },
            {
              number: '3.3.3',
              content:
                'Additional fees may apply if API usage exceeds the assigned quota.',
            },
            {
              number: '3.3.4',
              content:
                'The Enterprise plan includes SLA (Service Level Agreement) options, security certification integration, and other contract-based services.',
            },
            {
              number: '3.3.5',
              content:
                'Refunds, cancellations, and withdrawal policies follow Chapter 6 of the Integrated Terms of Service.',
            },
          ],
        },
        {
          title: '3.4 Feature Updates and Access Policy',
          items: [
            {
              number: '3.4.1',
              content:
                'Platform features may be updated or modified due to version upgrades, regulatory requirements, or third-party data policy changes.',
            },
            {
              number: '3.4.2',
              content:
                'Spacemap follows its Product Update Policy and provides prior notice for major updates.',
            },
            {
              number: '3.4.3',
              content:
                'New or experimental features may be introduced in a beta phase to ensure stable service delivery.',
            },
          ],
        },
      ],
    },
    {
      title: '4. Payment & Subscription Terms',
      subsections: [
        {
          title: '4.1 Purpose',
          items: [
            {
              number: '4.1.1',
              content:
                'This subsection states that the chapter defines payment methods, billing procedures, subscription conditions, and cancellation rules for the paid services of the Spacemap Platform. The objective is to ensure transparency, consistency, and fairness in all monetary transactions between the Company and the Customer.',
            },
          ],
        },
        {
          title: '4.2 Accepted Payment Methods',
          items: [
            {
              number: '4.2.1',
              content:
                'This subsection outlines the electronic payment methods supported by the Company for all paid subscription plans:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'PayPal: Payments are made through registered PayPal accounts. It is available for both individual and corporate Customers.',
                },
              ],
            },
            {
              number: '4.2.2',
              content:
                'All payments are processed securely through certified third-party payment processors. The Company does not store credit card or payment authentication data, in accordance with PCI-DSS and other applicable standards.',
            },
            {
              number: '4.2.3',
              content:
                'The Company may introduce additional payment methods in the future, and customers will be notified accordingly.',
            },
          ],
        },
        {
          title: '4.3 Subscription and Billing Cycle',
          items: [
            {
              number: '4.3.1',
              content:
                'All paid plans operate on a monthly recurring subscription model, which renews automatically unless cancelled by the Customer.',
            },
            {
              number: '4.3.2',
              content:
                'Advanced and Enterprise Customers have the option to request annual billing, provided it is subject to separate pricing or discounts that have been agreed upon in writing.',
            },
            {
              number: '4.3.3',
              content:
                "Invoices or payment confirmations will be delivered electronically to the Customer's registered email address following each successful billing cycle.",
            },
            {
              number: '4.3.4',
              content:
                'Billing will persist until the Customer formally submits an official cancellation request through an approved communication channel.',
            },
          ],
        },
        {
          title: '4.4 Taxes and Additional Charges',
          items: [
            {
              number: '4.4.1',
              content:
                'All subscription fees are denominated in USD (United States Dollars) unless explicitly stated otherwise.',
            },
            {
              number: '4.4.2',
              content:
                "Regional taxes, such as VAT (Value Added Tax), consumption tax, or digital service tax, may be applied. The applicability of these taxes depends on the Customer's billing address and relevant local regulations.",
            },
            {
              number: '4.4.3',
              content: 'Additional fees may apply for the following scenarios:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Excess API usage that goes beyond the standard quota.',
                },
                {
                  number: 'b.',
                  content:
                    'Custom analysis, consulting, or technical services.',
                },
                {
                  number: 'c.',
                  content: 'Enterprise-specific integrations or support.',
                },
                {
                  number: 'd.',
                  content:
                    'Such charges will be communicated to the customer in advance.',
                },
              ],
            },
          ],
        },
        {
          title: '4.5 Cancellation and Refunds',
          items: [
            {
              number: '4.5.1',
              content:
                'The Customer has the right to cancel their subscription at any time. The cancellation, however, will take effect at the end of the current billing cycle.',
            },
            {
              number: '4.5.2',
              content:
                'Payments already made are generally non-refundable, with the following exceptions:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Prolonged service outages or critical malfunctions that are directly caused by the Company.',
                },
                {
                  number: 'b.',
                  content:
                    'Duplicate charges or billing errors that are attributable to the Company.',
                },
              ],
            },
            {
              number: '4.5.3',
              content:
                'All cancellation requests must be submitted either through the Customer Portal or via other designated communication channels.',
            },
            {
              number: '4.5.4',
              content:
                'For Enterprise Customers, the rules governing refund, withdrawal, or termination will adhere to the terms outlined in their individual agreements (e.g., Service Level Agreement (SLA), Statement of Work (SOW)).',
            },
          ],
        },
        {
          title: '4.6 Fee Adjustments',
          items: [
            {
              number: '4.6.1',
              content:
                'The Company may modify subscription fees due to changes in service scope, operational costs, or regulatory requirements.',
            },
            {
              number: '4.6.2',
              content:
                "If the fee adjustment negatively affects the Customer, the Company shall provide a minimum of 30 days' prior notice.",
            },
            {
              number: '4.6.3',
              content:
                'Revised fees will apply only to billing cycles that begin after the effective date.',
            },
          ],
        },
        {
          title: '4.7 Payment Failure and Account Restrictions',
          items: [
            {
              number: '4.7.1',
              content:
                'If a payment attempt fails, a 7-day grace period will be granted before any service restrictions are applied.',
            },
            {
              number: '4.7.2',
              content:
                'If payment remains outstanding beyond the grace period, access to paid features may be suspended temporarily.',
            },
            {
              number: '4.7.3',
              content:
                'Repeated payment failures, abnormal billing activity, or suspected fraud may result in account termination.',
            },
          ],
        },
        {
          title: '4.8 Enterprise Billing',
          items: [
            {
              number: '4.8.1',
              content:
                'Enterprise Customers may elect to pay by invoice billing, subject to terms defined in their individual agreements.',
            },
            {
              number: '4.8.2',
              content:
                'Payment schedules (e.g., Net 15, Net 30), invoicing formats, and tax handling shall be determined within the Enterprise Agreement.',
            },
            {
              number: '4.8.3',
              content:
                'Enterprise Customers may still use credit card or PayPal payments; however, certain high-value transactions may require invoice billing due to internal financial control requirements.',
            },
          ],
        },
      ],
    },
    {
      title: '5. Refund & Cancellation Policy',
      subsections: [
        {
          title: '5.1 Purpose',
          items: [
            {
              number: '5.1.1',
              content:
                'This chapter defines the policies governing refunds, subscription cancellations, billing disputes, and credit adjustments related to paid services offered by the Spacemap Platform.',
            },
            {
              number: '5.1.2',
              content:
                'This chapter supplements Chapter 6 (Billing and Refund Policy) of the Integrated Terms of Service, which shall prevail in case of conflict.',
            },
          ],
        },
        {
          title: '5.2 Non-Refundable Policy',
          items: [
            {
              number: '5.2.1',
              content:
                'All subscription fees are charged on a prepaid, monthly basis and are non-refundable once payment is completed, even if the subscription is canceled mid-cycle.',
            },
            {
              number: '5.2.2',
              content:
                'Refunds will not be issued for reasons such as change of mind, insufficient usage, or misunderstanding of available features.',
            },
            {
              number: '5.2.3',
              content:
                'Temporary service limitations due to maintenance, system updates, or partial feature disruptions do not qualify for refunds as long as the core service remains operational.',
            },
          ],
        },
        {
          title: '5.3 Exceptions Where Refunds or Credits May Be Issued',
          items: [
            {
              number: '5.3.1',
              content:
                'The Company may provide refunds or service credits under the following circumstances:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Major Service Outage (SLA Breach): If the monthly service availability fails to meet the SLA threshold. Applicable only to Enterprise customers; SLA credits may be issued in the form of service extension or fee adjustment.',
                },
                {
                  number: 'b.',
                  content:
                    'Duplicate or Erroneous Payments: When duplicate charges occur for the same account. When billing errors result in overpayment beyond the correct amount.',
                },
                {
                  number: 'c.',
                  content:
                    "Service Disruption Caused by the Company: When long-term or critical service unavailability is attributable to the Company's fault. The Company may issue partial or full refunds for the affected billing period.",
                },
              ],
            },
          ],
        },
        {
          title: '5.4 Subscription Cancellation',
          items: [
            {
              number: '5.4.1',
              content:
                'Users may cancel their subscription at any time. Upon cancellation, future billing cycles will no longer be charged.',
            },
            {
              number: '5.4.2',
              content:
                'Fees already paid for the current billing period remain non-refundable.',
            },
            {
              number: '5.4.3',
              content:
                'For Enterprise customers, cancellation terms follow the separate Customer Agreement.',
            },
          ],
        },
        {
          title: '5.5 Billing Adjustments and Service Credits',
          items: [
            {
              number: '5.5.1',
              content:
                'If billing errors (e.g., incorrect API overage fees or misapplied charges) are identified, the Company may issue adjustments or service credits.',
            },
            {
              number: '5.5.2',
              content:
                'Credits carry the same effect as refunds for future billing but cannot be converted into cash.',
            },
            {
              number: '5.5.3',
              content:
                'Credits are valid for 12 months from the date of issuance and will automatically expire thereafter.',
            },
          ],
        },
        {
          title: '5.6 Billing Dispute Process',
          items: [
            {
              number: '5.6.1',
              content:
                'Users may submit a billing dispute within 30 days from the date of payment.',
            },
            {
              number: '5.6.2',
              content:
                'The Company will review the submitted documentation and respond within 10 business days.',
            },
            {
              number: '5.6.3',
              content:
                'If the dispute is deemed valid, the Company will implement corrective actions including refunds, credits, or billing adjustments.',
            },
          ],
        },
        {
          title: '5.7 Miscellaneous',
          items: [
            {
              number: '5.7.1',
              content:
                'Refunds and adjustments will be processed using the same payment method originally used (e.g., credit card or PayPal).',
            },
            {
              number: '5.7.2',
              content:
                "Processing time for refunds may vary depending on the payment processor's policies.",
            },
            {
              number: '5.7.3',
              content:
                'This policy may be updated in accordance with legal requirements or internal policy changes.',
            },
          ],
        },
      ],
    },
    {
      title: '6. Liability and Indemnification',
      subsections: [
        {
          title: '6.1 Purpose and Scope',
          items: [
            {
              number: '6.1.1',
              content:
                'The purpose of this Chapter is to define the liability and indemnification framework specifically related to the pricing, billing, subscription, and refund policies of the Spacemap Platform, in order to prevent disputes between the Company and the User.',
            },
            {
              number: '6.1.2',
              content:
                'This Chapter applies only to matters directly related to the Pricing Policy, and the overall liability principles governing the service are subject to the B2B Customer Agreement and the Integrated Terms of Service.',
            },
            {
              number: '6.1.3',
              content:
                'In the event of any conflict between this Chapter and a higher-level agreement, the provisions of the higher-level agreement shall prevail.',
            },
          ],
        },
        {
          title: '6.2 General Disclaimer',
          items: [
            {
              number: '6.2.1',
              content:
                'The Company shall use commercially reasonable efforts to provide the features and services corresponding to each subscription plan.',
            },
            {
              number: '6.2.2',
              content:
                'The Company shall not be liable for any damages arising from the following circumstances:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    "Failures or limitations related to the User's devices, network, browser, or system environment",
                },
                {
                  number: 'b.',
                  content:
                    'Outages or policy changes of external payment service providers (e.g., PG providers, card issuers, PayPal)',
                },
                {
                  number: 'c.',
                  content:
                    'Force majeure events, including natural disasters, war, power outages, or network failures',
                },
                {
                  number: 'd.',
                  content:
                    'Violations of this Policy or higher-level agreements by the User',
                },
                {
                  number: 'e.',
                  content:
                    'Delays or discrepancies resulting from data update cycles or policy changes of third-party data sources',
                },
              ],
            },
          ],
        },
        {
          title: '6.3 Limitation of Liability',
          items: [
            {
              number: '6.3.1',
              content:
                "The Company's monetary liability related to billing or subscription matters shall not exceed the actual amount paid by the User for the relevant month.",
            },
            {
              number: '6.3.2',
              content: 'The Company shall not be liable for:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Indirect, special, or consequential damages, including lost profits or loss of business opportunities',
                },
                {
                  number: 'b.',
                  content:
                    'Damages arising from data loss, service interruption, or related secondary impacts',
                },
                {
                  number: 'c.',
                  content:
                    "Damages caused by the User's failure to comply with internal approval or accounting procedures",
                },
              ],
            },
            {
              number: '6.3.3',
              content:
                'Any credits or compensation applicable to Enterprise subscriptions under the SLA shall not exceed the limits specified in the applicable SLA or the B2B Customer Agreement.',
            },
          ],
        },
        {
          title: '6.4 User Indemnification',
          items: [
            {
              number: '6.4.1',
              content:
                'The Company may restrict services, suspend accounts, terminate agreements, or claim damages if the User engages in any of the following activities, and the User shall be liable for all resulting damages:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    "Providing false billing information or using another party's payment method without authorization",
                },
                {
                  number: 'b.',
                  content:
                    'Concealing API usage, operating split accounts, or otherwise attempting to circumvent billing',
                },
                {
                  number: 'c.',
                  content:
                    'Causing system overload or failures through abnormal API usage',
                },
                {
                  number: 'd.',
                  content:
                    'Abusing refund processes, submitting fraudulent disputes, or manipulating payment processes',
                },
              ],
            },
            {
              number: '6.4.2',
              content:
                "Actions taken by the User's employees, partners, agents, contractors, or subcontractors shall be deemed actions of the User, and the User shall bear full responsibility.",
            },
          ],
        },
        {
          title: '6.5 Relationship with Higher-Level Agreements',
          items: [
            {
              number: '6.5.1',
              content:
                'This Chapter serves as a supplementary provision specific to pricing, billing, and subscription matters. The overall liability structure of the service is governed by:',
              subItems: [
                {
                  number: 'a.',
                  content: 'The B2B Customer Agreement',
                },
                {
                  number: 'b.',
                  content: 'The Integrated Terms of Service',
                },
              ],
            },
            {
              number: '6.5.2',
              content:
                'If any provision of this Chapter conflicts with a higher-level agreement, the higher-level agreement shall prevail, and this Chapter shall be applied only as a supplementary interpretative guideline for pricing-related matters.',
            },
          ],
        },
      ],
    },
    {
      title: '7. Governing Law and Dispute Resolution',
      subsections: [
        {
          title: '7.1 Purpose',
          items: [
            {
              number: '7.1.1',
              content:
                'The purpose of this Chapter is to establish procedures and standards for handling objections and disputes related to the pricing, billing, subscription, and refund policies of the Spacemap Platform in a fair and efficient manner.',
            },
          ],
        },
        {
          title: '7.2 Governing Law',
          items: [
            {
              number: '7.2.1',
              content:
                'This Policy and all billing, payment, refund, and adjustment activities arising under it shall be governed by the laws of the Republic of Korea.',
            },
            {
              number: '7.2.2',
              content:
                'Even when the Service is used outside Korea or when foreign payment methods are utilized, the laws of the Republic of Korea shall apply unless otherwise agreed in writing. Tax or foreign exchange regulations of other jurisdictions may apply in a supplementary manner where necessary.',
            },
          ],
        },
        {
          title: '7.3 Billing Dispute Process',
          items: [
            {
              number: '7.3.1',
              content:
                'If a User has objections regarding billing amounts, refund eligibility, or credit applications, they must submit a written dispute request via the customer support portal or designated email within 30 days of the billing date.',
            },
            {
              number: '7.3.2',
              content:
                'Upon receiving a dispute request, the Company will review the case and notify the User of the result within 10 business days. If additional documentation is required, the Company may request supplementary information, and the review period may be reasonably adjusted.',
            },
            {
              number: '7.3.3',
              content:
                'Both parties shall act in good faith to resolve disputes through mutual consultation. Disputes not resolved through this process will follow the dispute resolution procedures defined in higher-level agreements (the B2B Customer Agreement and the Integrated Terms of Service).',
            },
          ],
        },
        {
          title: '7.4 Jurisdiction',
          items: [
            {
              number: '7.4.1',
              content:
                'Any legal action arising in connection with this Policy shall be brought before the Seoul Central District Court, which will have exclusive jurisdiction as the court of first instance.',
            },
            {
              number: '7.4.2',
              content:
                'If the parties have agreed to a different competent court in a separate B2B agreement, such agreement shall prevail.',
            },
          ],
        },
      ],
    },
    {
      title: '8. Final Provisions',
      subsections: [
        {
          title: '8.1 Purpose',
          items: [
            {
              number: '8.1.1',
              content:
                'This Chapter sets forth the effective date, amendment procedures, document hierarchy, language rules, and severability applicable to the Spacemap Platform Pricing Policy to clarify its final legal effect.',
            },
          ],
        },
        {
          title: '8.2 Amendments',
          items: [
            {
              number: '8.2.1',
              content:
                'The Company may amend this Policy as necessary due to changes in the service structure, external data or infrastructure costs, or applicable laws and regulatory requirements.',
            },
            {
              number: '8.2.2',
              content:
                'If an amendment may materially or adversely affect the User, the Company shall provide prior notice no less than 30 days before the effective date via the Spacemap portal, email, or other reasonable means.',
            },
            {
              number: '8.2.3',
              content:
                "Minor revisions that do not materially impact the User's rights or obligations—such as typographical corrections or updates to references—may be implemented immediately and shall be deemed notified upon publication in the policy version history.",
            },
          ],
        },
        {
          title: '8.3 Effective Date',
          items: [
            {
              number: '8.3.1',
              content:
                'This Policy shall become effective on the date specified herein or on any separately announced effective date.',
            },
            {
              number: '8.3.2',
              content:
                'For existing Users, the amended Policy shall apply from the first billing cycle following the effective date.',
            },
          ],
        },
        {
          title: '8.4 Document Hierarchy',
          items: [
            {
              number: '8.4.1',
              content:
                'In the event of any inconsistency or conflict between this Policy and other governing documents, the order of precedence shall be:',
              subItems: [
                {
                  number: 'a.',
                  content: 'B2B Customer Agreement',
                },
                {
                  number: 'b.',
                  content: 'Integrated Terms of Service',
                },
                {
                  number: 'c.',
                  content: 'This Pricing Policy (Annex B – Platform)',
                },
                {
                  number: 'd.',
                  content:
                    'Operational guidelines, product documentation, and other reference materials',
                },
              ],
            },
            {
              number: '8.4.2',
              content:
                'This Policy serves as a supplementary document specifically concerning pricing matters and shall not override the interpretation of higher-level agreements.',
            },
          ],
        },
        {
          title: '8.5 Language',
          items: [
            {
              number: '8.5.1',
              content:
                'This Policy is written in Korean. In the event of any inconsistency between the Korean version and any translation, the Korean version shall prevail.',
            },
          ],
        },
        {
          title: '8.6 Severability',
          items: [
            {
              number: '8.6.1',
              content:
                'If any provision of this Policy is held to be invalid, illegal, or unenforceable, the remaining provisions shall remain in full force and effect.',
            },
          ],
        },
      ],
    },
  ],
};

export const normalPricingContentKR: PricingContent = {
  title: 'PRICING POLICY',
  sections: [
    {
      title: '1. 총칙',
      subsections: [
        {
          title: '1.1 목적',
          items: [
            {
              number: '',
              content:
                '본 약관은 SPACEMAP(이하 "회사")이 운영하는 Spacemap Platform(이하 "플랫폼")의 서비스 이용과 관련하여, 회사와 회원 간의 권리·의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 한다. 약관은 개인 및 사업자(법인, 기관, 단체 등) 회원 모두에게 적용된다.',
            },
          ],
        },
        {
          title: '1.2 정의',

          items: [
            {
              number: '',
              content: '본 약관에서 사용하는 용어의 정의는 다음과 같다.',
            },
            {
              number: '1.2.1',
              content:
                '“서비스”란 회사가 운영하는 웹사이트 및 모바일 앱을 통해 제공하는 위성·우주데이터 기반 분석, 시각화, 궤도예측, 충돌경보, API 제공, 교육 및 학습 콘텐츠 제공 등 일체의 온라인 서비스 및 이에 부수하는 기술지원·컨설팅을 포함한다.',
            },
            {
              number: '1.2.2',
              content:
                '“회원”이란 서비스에 가입하여 계정을 발급받고 회사가 제공하는 기능을 이용하는 자를 말한다.',
            },
            {
              number: '1.2.3',
              content:
                '“기업회원(사업자회원)”이란 사업자등록번호 또는 기관명으로 가입한 법인, 연구기관, 정부기관, 또는 그에 준하는 단체를 의미한다.',
            },
            {
              number: '1.2.4',
              content:
                '“일반회원(개인회원)”이란 개인 자격으로 가입하여 서비스를 이용하는 회원을 의미한다.',
            },
            {
              number: '1.2.5',
              content:
                '"데이터"란 회사가 플랫폼을 통하여 제공하는 모든 형태의 정보로서, 위성 궤도요소, 충돌위험 예측정보, 우주상황인식(SSA) 자료, 궤적 시뮬레이션, 분석결과, 시각화 콘텐츠, API 응답값, 교육 및 학습 콘텐츠 등을 포함한다.',
            },
            {
              number: '1.2.6',
              content:
                '“API(Application Programming Interface)”란 제3자가 회사의 시스템에 연동하여 데이터를 호출하거나 기능을 이용할 수 있도록 제공되는 프로그래밍 인터페이스를 말한다.',
            },
            {
              number: '1.2.7',
              content:
                '“유료서비스”란 일정한 대가를 지불하고 이용할 수 있는 기능, 데이터 접근권한, 또는 구독형 상품을 의미한다.',
            },
            {
              number: '1.2.8',
              content:
                '“무료서비스”란 회사가 무상으로 제공하는 기능 또는 체험형 서비스를 의미한다.',
            },
            {
              number: '1.2.9',
              content:
                '“개별계약”이란 본 약관 외에 별도로 체결되는 서비스 이용계약, 데이터 라이선스 계약 또는 기술협력 계약을 의미한다.',
            },
          ],
        },
        {
          title: '1.3 약관의 효력 및 변경',
          items: [
            {
              number: '1.3.1',
              content:
                '본 약관은 플랫폼 웹사이트에 게시함으로써 그 효력이 발생한다.',
            },
            {
              number: '1.3.2',
              content:
                '회사는 필요 시 관련 법령을 위반하지 않는 범위 내에서 본 약관을 개정할 수 있으며, 변경 시 시행일 및 변경사유를 명시하여 현행약관과 함께 플랫폼 웹사이트에 게시하는 방법으로 최소 7일 전부터 시행일 전일까지 공지한다. 단, 변경 내용이 회원에게 불리하거나 회원의 권리 또는 의무에 관한 중요 사항인 경우에는 최소 30일 전부터 시행일 전일까지 공지하고 회원에게 통지한다.',
            },
            {
              number: '1.3.3',
              content:
                '회원은 변경된 약관의 적용에 동의하지 않는 경우, 서비스 이용을 중단하고 회원탈퇴를 요청할 수 있다.',
            },
            {
              number: '1.3.4',
              content:
                '회사가 변경된 약관을 공지하거나 통지하면서 회원에게 약관 변경 시행일 전일까지 거부 의사를 표시하지 않으면 약관의 변경에 동의한 것으로 간주한다는 내용을 공지 또는 통지하였음에도 회원이 명시적으로 약관 변경에 대한 거부 의사를 표시하지 않았을 때 회사는 회원이 변경된 약관에 동의한 것으로 간주한다.',
            },
          ],
        },
        {
          title: '1.4 약관 외 준칙, 개별계약',
          items: [
            {
              number: '1.4.1',
              content:
                '본 약관에서 정하지 않은 사항과 본 약관의 해석에 관하여는 관련 법령에 따른다.',
            },
            {
              number: '1.4.2',
              content:
                '회사는 회원과 개별계약을 체결할 수 있으며, 개별계약과 본 약관과 개별계약의 내용이 상충하는 경우, 개별계약의 규정이 우선한다.',
            },
            {
              number: '1.4.3',
              content:
                '회사는 서비스의 일부에 대하여 별도의 세부정책(예: 데이터 정책, 보안정책, API 이용정책, 요금정책 등)을 둘 수 있으며, 회사는 세부정책의 내용을 회원이 알 수 있도록 서비스 내 또는 그 연결화면에 게시한다. ',
            },
            {
              number: '1.4.4',
              content:
                '회사는 세부정책 변경 시 1.3.2.의 절차에 따른다. 다만, 세부정책 변경 내용이 다음 각 호의 어느 하나에 해당하는 경우에는 전항의 방법으로 사전에 공지한다.',
              subItems: [
                {
                  number: 'a.',
                  content: '본 약관에서 구체적으로 위임한 사항을 변경하는 경우',
                },
                {
                  number: 'b.',
                  content: '회원의 권리∙의무와 관련 없는 사항을 개정하는 경우',
                },
                {
                  number: 'c.',
                  content:
                    '세부정책의 내용이 본 약관에서 정한 내용과 근본적으로 다르지 않고 회원이 예측할 수 있는 범위 내에서 세부정책을 변경하는 경우',
                },
              ],
            },
            {
              number: '1.4.5',
              content:
                '세부정책은 본 약관의 일부로 간주되며, 세부정책과 본 약관이 상충되는 경우 본 약관이 우선한다.',
            },
          ],
        },
      ],
    },
    {
      title: '2. 서비스의 제공 및 이용',
      subsections: [
        {
          title: '2.1 서비스의 구성 및 종류',
          items: [
            {
              number: '2.1.1',
              content:
                '회사가 제공하는 서비스의 기본 구성은 다음과 같고, 각기 다른 사이트 또는 어플리케이션에서 제공될 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '위성 및 궤도 데이터 서비스: 위성체 궤도요소(TLE), 운용현황, 충돌위험도 등 실시간·분석 데이터 제공',
                },
                {
                  number: 'b.',
                  content:
                    '우주상황인식(SSA) 및 충돌예측 서비스: 위성 간 충돌확률, 위험 구역 예측, 근접 접근 이벤트(Conjunction Event) 분석',
                },
                {
                  number: 'c.',
                  content:
                    'API 제공 서비스: 외부 개발자 및 기관이 API를 통해 데이터에 접근·조회·가공할 수 있도록 하는 인터페이스 제공',
                },
                {
                  number: 'd.',
                  content:
                    '시각화 대시보드: 위성의 궤도·지상궤적·위험지도 등 그래픽 기반 인터페이스 제공',
                },
                {
                  number: 'e.',
                  content:
                    '분석 보고서 및 리포트 서비스: 주기적 또는 요청 기반으로 생성되는 분석결과·PDF 보고서 등',
                },
                {
                  number: 'f.',
                  content:
                    '교육 및 학습 콘텐츠 제공 서비스: 회사의 위성·우주 데이터에 기반한 이러닝 콘텐츠, 가상 인공위성∙우주선을 제어하고 운영하는 시뮬레이션 기능, 위성 찾기 기능 등 제공',
                },
                {
                  number: 'g.',
                  content:
                    '기타 부가서비스: 기술지원, 데이터 컨설팅, 협력기관 연동 서비스 등',
                },
              ],
            },
            {
              number: '2.1.2',
              content:
                '서비스의 구체적 내용은 회사의 정책 또는 별도 안내에 따라 추가·변경될 수 있다.',
            },
            {
              number: '2.1.3',
              content:
                '회사는 서비스의 일부를 무료로 제공하거나, 유료서비스로 구분하여 이용료를 부과할 수 있다.',
            },
          ],
        },
        {
          title: '2.2 서비스 이용계약의 성립',
          items: [
            {
              number: '2.2.1',
              content:
                '서비스 이용계약은 회원이 본 약관에 동의하고, 회원가입 또는 별도의 계약체결 절차를 완료함으로써 성립한다.',
            },
            {
              number: '2.2.2',
              content:
                '회사는 다음 각 호의 어느 하나에 해당하는 신청에 대하여는 승낙을 거절하거나 사후에 취소할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content: '만 14세 미만인 경우',
                },
                {
                  number: 'b.',
                  content: '허위의 정보로 가입 신청을 한 경우',
                },
                {
                  number: 'c.',
                  content: '서비스를 상업적·비윤리적 이용을 목적으로 하는 경우',
                },
                {
                  number: 'd.',
                  content:
                    '국가안보, 수출통제 등 법령에 따라 접근이 제한된 국가 또는 기관의 경우',
                },
                {
                  number: 'e.',
                  content:
                    '타인의 권리를 침해하거나, 회사의 서비스 운영을 방해한 이력이 있는 경우',
                },
                {
                  number: 'f.',
                  content:
                    '기타 회사가 서비스 제공에 부적절하다고 판단하는 경우',
                },
              ],
            },
            {
              number: '2.2.3',
              content:
                '사업자회원은 법인의 명의로 가입할 수 있으며, 이 경우 법인의 권한 있는 대리인이 신청해야 한다.',
            },
            {
              number: '2.2.4',
              content:
                '회사는 필요 시 신청자의 신원확인 또는 사업자등록증 등의 제출을 요구할 수 있다.',
            },
          ],
        },
        {
          title: '2.3 계정의 생성 및 관리',
          items: [
            {
              number: '2.3.1',
              content:
                '회원은 서비스 이용을 위해 계정을 생성해야 하며, 계정의 ID 및 비밀번호, API 키 등의 관리책임은 전적으로 회원에게 있다.',
            },
            {
              number: '2.3.2',
              content:
                '회원은 자신의 계정을 제3자에게 대여·양도하거나, 공유할 수 없다.',
            },
            {
              number: '2.3.3',
              content:
                '기업회원의 경우, 대표계정(마스터 계정)을 통해 소속 사용자(Sub-user)를 관리할 수 있으며, 각 서브계정의 이용행위에 대한 책임은 기업회원에게 있다.',
            },
            {
              number: '2.3.4',
              content:
                '기업회원 소속 사용자의 서브계정은 아래 각 호의 경우, 일반회원으로 전환된다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '기업회원이 회사와 체결한 개별계약이 종료(해제나 해지 포함)되는 경우',
                },
                {
                  number: 'b.',
                  content:
                    '기업회원 또는 서브계정을 보유한 회원이 기업회원과 서브계정의 연결 철회를 요구하는 경우',
                },
              ],
            },
          ],
        },
        {
          title: '2.4 서비스 가용성',
          items: [
            {
              number: '2.4.1',
              content:
                '서비스의 기본 이용시간은 연중무휴 24시간을 원칙으로 하나, 시스템 점검, 데이터 업데이트, 네트워크 장애 등 기술적 사유로 일시적 중단이 있을 수 있다.',
            },
            {
              number: '2.4.2',
              content:
                '회사는 서비스의 안정적 제공을 위해 사전 통지 후 시스템 점검을 실시할 수 있으며, 긴급한 보안위험이 있는 경우 사전 통지 없이 중단할 수 있다.',
            },
            {
              number: '2.4.3',
              content:
                '회원의 서비스 접근권한은 계정 유형(일반, 기업, 관리자 등), 결제상태, 라이선스 범위 등에 따라 제한될 수 있다.',
            },
            {
              number: '2.4.4',
              content:
                'API 사용량이 과도하거나 시스템 부하가 발생할 경우, 회사는 일시적으로 호출량(rate limit)을 제한할 수 있다',
            },
          ],
        },
        {
          title: '2.5 서비스 변경 및 업데이트',
          items: [
            {
              number: '2.5.1',
              content:
                '회사는 서비스의 향상, 안정성 확보, 기술적 필요 또는 법령 준수를 위하여 서비스의 전부 또는 일부를 변경할 수 있다.',
            },
            {
              number: '2.5.2',
              content:
                '서비스 내용이 변경되는 경우, 회사는 그 내용을 사전에 공지하며, 회원에게 불리한 변경인 경우 최소 30일 전에 통지한다.',
            },
            {
              number: '2.5.3',
              content:
                '회사는 새로운 기능이나 유료서비스를 도입할 수 있으며, 그에 따른 요금정책은 별도로 공지한다.',
            },
            {
              number: '2.5.4',
              content:
                '기술적 사유 또는 제3자 데이터 제공정책의 변경으로 인해 일부 기능이 축소·폐지될 수 있으며, 회사는 이에 대해 고의·과실이 없는 한 책임을 부담하지 않는다.',
            },
          ],
        },
        {
          title: '2.6 서비스 이용의 제한 및 정지',
          items: [
            {
              number: '2.6.1',
              content:
                '회사는 다음 각 호의 사유가 발생한 경우, 이용제한 조치의 사유, 이용제한 조치의 유형, 이용제한 조치에 대한 이의신청 방법을 사전 통보 후 서비스 이용을 제한하거나 정지할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content: '회원이 법령 또는 본 약관을 위반한 경우',
                },
                {
                  number: 'b.',
                  content:
                    '타인의 권리를 침해하거나 회사의 지적재산권을 침해한 경우',
                },
                {
                  number: 'c.',
                  content:
                    '해킹, 데이터 변조, 비인가 API 호출 등 시스템 위협 행위를 한 경우',
                },
                {
                  number: 'd.',
                  content: '서비스 요금의 미납 또는 부정결제 발생 시',
                },
                {
                  number: 'e.',
                  content: '국가기관의 명령 또는 법적 조치가 있는 경우',
                },
              ],
            },
            {
              number: '2.6.2',
              content:
                '긴급한 보안사고 또는 시스템 위협이 발생한 경우, 회사는 사전 통보 없이 즉시 이용정지를 할 수 있으며, 사후에 그 사유를 통지한다.',
            },
            {
              number: '2.6.3',
              content:
                '회원이 회사의 이용제한 조치에 불복하고자 할 때에는 해당 조치의 통지를 받은 날부터 14일 이내에 불복 이유를 기재한 이의 신청서를 서면, 전자우편 또는 이에 준하는 방법으로 회사에 제출하여야 한다. 회사는 회원의 이의신청서를 접수한 날부터 15일 이내(단, 이 기간 내에 답변이 어려운 경우 회사는 그 사유와 처리일정을 통지한다)에 불복 이유에 대하여 서면, 전자우편 또는 이에 준하는 방법으로 답변하며, 회사는 회원의 불복 이유가 타당한 경우에는 이에 따른 조치를 취한다.',
            },
            {
              number: '2.6.4',
              content:
                '서비스 이용이 제한·정지된 회원은 제한사유가 해소된 후 회사의 승인을 받아 이용을 재개할 수 있다.',
            },
            {
              number: '2.6.5',
              content:
                '회사는 이용정지로 인해 발생한 손해에 대해, 회사의 고의 또는 중대한 과실이 없는 한 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '2.7 베타서비스 및 시험운용',
          items: [
            {
              number: '2.7.1',
              content:
                '회사는 정식 출시 이전의 기능을 “베타서비스” 형태로 제공할 수 있으며, 베타서비스는 안정성이 보장되지 않을 수 있다.',
            },
            {
              number: '2.7.2',
              content:
                '베타서비스 회원은 오류 보고 및 피드백 제공을 요청받을 수 있으며, 회사는 피드백을 서비스 개선에 활용할 수 있다.',
            },
            {
              number: '2.7.3',
              content:
                '회사는 베타서비스 중 발생한 데이터 손실, 기능 오류 등에 대하여 법적 책임을 지지 않는다. 단, 회사의 고의 또는 중과실로 인한 경우는 그러하지 아니하다.',
            },
            {
              number: '2.7.4',
              content: '베타서비스는 별도 공지 없이 중단·종료될 수 있다.',
            },
          ],
        },
        {
          title: '2.8 공공안전 및 데이터 접근 제한',
          items: [
            {
              number: '2.8.1',
              content:
                '회사는 국가안보, 우주물체 충돌위험, 국제적 안전사고 등 공공목적상 필요하다고 판단되는 경우, 특정 데이터 또는 기능의 접근을 일시적으로 제한하거나, 관할기관에 데이터를 제공할 수 있다.',
            },
            {
              number: '2.8.2',
              content:
                '이 경우 회사는 관련 법령 및 국제협약(예: 우주파편 상호통보 협정 등)에 따라 필요한 범위 내에서 조치를 취한다.',
            },
            {
              number: '2.8.3',
              content:
                '회사는 해당 조치로 인해 회원에게 발생한 손해에 대하여, 고의 또는 중대한 과실이 없는 한 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '2.9 데이터 품질 및 정확성 관련 고지',
          items: [
            {
              number: '2.9.1',
              content:
                '회사는 위성궤도, 충돌예측, 궤적분석 등 데이터가 과학적 모델 및 외부 관측자료에 기반한 추정치임을 명확히 고지한다.',
            },
            {
              number: '2.9.2',
              content:
                '서비스에서 제공되는 데이터 및 분석결과는 참고자료로서 활용되어야 하며, 회원의 의사결정(내부 연구∙분석, 기술검증, 제품 개발, 위성운용, 발사계획, 보험계약 등)에 대한 법적·경제적 책임은 회원에게 있다.',
            },
            {
              number: '2.9.3',
              content:
                '회사는 데이터의 정확성·완전성·시의성을 보증하지 않으며, 외부기관의 데이터 오류·지연으로 인한 손해에 대해서도 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '2.10 기술지원',
          items: [
            {
              number: '2.10.1',
              content:
                '회사는 서비스 이용과 관련하여 기술적 문의 및 지원을 위한 채널(이메일, 헬프데스크, 온라인 티켓 등)을 운영한다.',
            },
            {
              number: '2.10.2',
              content:
                '기술지원의 범위는 서비스 안정적 이용을 위한 기본 수준으로 한정되며, 맞춤형 분석, 컨설팅 등 추가 기술지원은 별도의 계약이 필요하거나, 요금이 부과될 수 있다.',
            },
            {
              number: '2.10.3',
              content:
                '회사는 고객문의에 대해 최대한 신속하게 대응하되, 문의량, 난이도, 또는 외부 요인에 따라 응답이 지연될 수 있다.',
            },
          ],
        },
      ],
    },
    {
      title: '3. 유료 서비스 및 결제',
      subsections: [
        {
          title: '3.1 유료 서비스의 정의',
          items: [
            {
              number: '3.1.1',
              content:
                '“유료 서비스”란 회사가 회원에게 일정한 대가를 받고 제공하는 모든 형태의 기능, 데이터 접근권, API 호출권, 분석 리포트, 커스텀 기술지원 등을 말한다.',
            },
            {
              number: '3.1.2',
              content: '유료 서비스의 구체적 내용은 다음 각 호와 같다.',
              subItems: [
                {
                  number: 'a.',
                  content: '기본 플랫폼 유료 서비스',
                  subItems: [
                    {
                      number: 'i.',
                      content: 'API 이용권 (API Call Credit)',
                    },
                    {
                      number: 'ii.',
                      content:
                        '데이터 다운로드 패키지 (위성궤도·충돌예측 데이터셋 등)',
                    },
                    {
                      number: 'iii.',
                      content:
                        '시각화 대시보드의 고급 기능 (다중 위성 추적, 충돌 시뮬레이션 등)',
                    },
                    {
                      number: 'iv.',
                      content: '분석 리포트 및 자동 보고서 구독 서비스',
                    },
                    {
                      number: 'v.',
                      content: '커스텀 기술지원 및 컨설팅 서비스',
                    },
                  ],
                },
                {
                  number: 'b.',
                  content: '교육 및 학습 콘텐츠 제공 서비스',
                },
              ],
            },
            {
              number: '3.1.3',
              content:
                '회사는 서비스별 가격, 이용기간, 결제방식 등을 플랫폼 내 요금정책 페이지에 명시한다.',
            },
            {
              number: '3.1.4',
              content:
                '회사는 신규 상품을 추가하거나 기존 상품의 구성, 명칭, 요금을 변경할 수 있으며, 변경사항은 사전에 공지한다.',
            },
          ],
        },
        {
          title: '3.2 요금정책 및 과금기준',
          items: [
            {
              number: '3.2.1',
              content:
                '서비스 요금은 회원의 사용량, 계정유형, API 호출 횟수, 저장공간, 분석 범위 등에 따라 차등 적용될 수 있다.',
            },
            {
              number: '3.2.2',
              content:
                '회사는 회원에게 과금 방식(정액제, 종량제, 구독형 등)을 명시하고, 회원은 이에 따라 요금을 부담한다.',
            },
            {
              number: '3.2.3',
              content:
                '서비스 이용요금은 부가가치세(VAT) 등 관련 세금을 포함하거나 별도로 부과할 수 있으며, 세부 내역은 결제 페이지 또는 영수증에 표시된다.',
            },
            {
              number: '3.2.4',
              content:
                '기업회원은 세금계산서 발행을 요청할 수 있으며, 회사는 내부 절차에 따라 발행한다.',
            },
            {
              number: '3.2.5',
              content:
                '회사는 결제 오류 또는 이중과금이 발생한 경우, 회원의 요청에 따라 확인 후 적절한 조치를 취한다.',
            },
            {
              number: '3.2.6',
              content:
                '회사는 별도의 가격정책을 둘 수 있으며, 이 약관의 내용을 이룬다.',
            },
          ],
        },
        {
          title: '3.3 결제방식',
          items: [
            {
              number: '3.3.1',
              content:
                '회원은 다음 각 호의 방식으로 서비스 요금을 결제할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content: '신용카드 또는 직불카드',
                },
                {
                  number: 'b.',
                  content: '계좌이체 또는 가상계좌',
                },
                {
                  number: 'c.',
                  content:
                    '전자지갑 또는 결제대행서비스(PG)를 통한 온라인 결제',
                },
                {
                  number: 'd.',
                  content:
                    '기업회원의 경우 별도 계약에 따른 후불제 또는 송장 결제 방식',
                },
              ],
            },
            {
              number: '3.3.2',
              content:
                '회원이 결제수단을 부정하게 사용하거나 타인의 결제정보를 도용한 경우, 회사는 즉시 서비스 이용을 중단하고 법적 조치를 취할 수 있다.',
            },
            {
              number: '3.3.3',
              content:
                '결제 관련 분쟁은 해당 결제대행사의 정책 및 관련 법령에 따른다.',
            },
          ],
        },
        {
          title: '3.4 이용기간 및 자동갱신',
          items: [
            {
              number: '3.4.1',
              content:
                '유료 서비스의 이용기간은 결제 시 명시된 기간(예: 월간, 연간 등)으로 한다.',
            },
            {
              number: '3.4.2',
              content:
                '구독형 서비스의 경우, 별도 해지 요청이 없는 한 동일한 조건으로 자동갱신된다.',
            },
            {
              number: '3.4.3',
              content:
                '회원은 자동갱신을 원하지 않을 경우, 갱신 예정일 이전에 해지를 신청해야 한다.',
            },
            {
              number: '3.4.4',
              content:
                '자동갱신으로 인한 요금이 결제된 이후에는 환불이 제한될 수 있다.',
            },
            {
              number: '3.4.5',
              content:
                '회원이 유료 서비스 이용기간 중 세부 플랜(등급)을 변경하는 경우의 처리는 별도의 가격정책에서 정한다.',
            },
          ],
        },
        {
          title: '3.5 환불정책',
          items: [
            {
              number: '3.5.1',
              content:
                '유료 서비스 결제 후 회원 또는 회사가 계약을 해지할 경우, 회사는 다음 기준에 따라 환불한다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '결제 후 7일 이내로서 서비스 이용이 시작되지 않은 경우: 전액 환불',
                },
                {
                  number: 'b.',
                  content:
                    '서비스 이용을 일부라도 시작한 경우 또는 결제 후 7일이 경과한 경우: 잔여기간에 대하 사용료를 일할계산한 금액을 공제 후 환불. 단, 환불을 위한 비용이 발생하는 경우 해당 실비를 공제하고 환불할 수 있다.',
                },
                {
                  number: 'c.',
                  content:
                    '전 호의 경우, 회원이 장기 구독을 선택하여 할인·프로모션 요금으로 상품(서비스)을 결제하였더라도 할인·프로모션을 적용하지 않은 요금(결제 시점의 정상가격)을 기준으로 환불 대금을 계산한다.',
                },
              ],
            },
            {
              number: '3.5.2',
              content:
                '전 항에도 불구하고, 현행법령 및 중대한 약관 위반 등 회원의 귀책사유로 이용계약을 해지하는 경우 환불이 제한될 수 있다.',
            },
            {
              number: '3.5.3',
              content:
                '환불금액은 결제수단 및 PG사 처리 일정에 따라 3영업일 이내에 지급될 수 있다.',
            },
            {
              number: '3.5.4',
              content:
                '회사는 환불 처리 시 송금 수수료 등 제반 비용을 공제할 수 있다. 단, 회사의 귀책사유로 인한 경우 기타 관련 법령상 비용의 공제가 제한되는 경우에는 그러하지 아니하다.',
            },
            {
              number: '3.5.5',
              content:
                '기업회원과의 계약 해지 시 환불에 관한 사항은 개별계약에 따른다.',
            },
          ],
        },
        {
          title: '3.6 요금 미납 및 결제 실패',
          items: [
            {
              number: '3.6.1',
              content:
                '회원이 결제 기한 내 요금을 납부하지 않을 경우, 회사는 서비스 이용을 일시 중단하거나 계정을 제한할 수 있다.',
            },
            {
              number: '3.6.2',
              content:
                '미납 상태가 30일 이상 지속될 경우, 회사는 계약을 해지하고 해당 계정을 삭제할 수 있다.',
            },
            {
              number: '3.6.3',
              content:
                '기업회원의 경우, 계약서에서 별도로 정한 납부기한을 초과한 경우 연체이자 또는 서비스 이용 제한이 적용될 수 있다.',
            },
            {
              number: '3.6.4',
              content:
                '회사는 요금 미납 또는 결제 실패로 인해 발생한 손해에 대해 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '3.7 환율',
          items: [
            {
              number: '3.7.1',
              content:
                '해외 결제의 경우, 결제 통화 및 환율은 결제 시점의 카드사 또는 결제대행사의 기준을 따른다.',
            },
            {
              number: '3.7.2',
              content:
                '회사는 외화 결제 시 발생할 수 있는 환율 변동 및 수수료에 대해 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '3.8 유료 서비스 변경 및 중단',
          items: [
            {
              number: '3.8.1',
              content:
                '회사는 운영상 또는 기술상의 필요에 따라 유료 서비스의 전부 또는 일부를 변경하거나 종료할 수 있다.',
            },
            {
              number: '3.8.2',
              content:
                '유료 서비스가 중단되는 경우, 회사는 중단 사유 및 일정을 최소 30일 이전에 공지한다.',
            },
            {
              number: '3.8.3',
              content:
                '서비스 중단으로 인해 미사용 잔여기간이 있는 회원에게는 잔여기간에 해당하는 요금을 환불한다.',
            },
            {
              number: '3.8.4',
              content:
                '회사는 천재지변, 정부 명령, 제3자 데이터 제공 중단 등 불가피한 사유로 인한 서비스 종료에 대해서는 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '3.9 부정사용 및 환수조치',
          items: [
            {
              number: '3.9.1',
              content:
                '회원이 다음 각 호의 행위를 한 경우, 회사는 이용계약을 해지하거나 이미 결제된 금액을 환수할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content: '타인의 계정 또는 결제수단을 무단으로 사용한 경우',
                },
                {
                  number: 'b.',
                  content: '데이터를 무단 복제·판매·재배포한 경우',
                },
                {
                  number: 'c.',
                  content: 'API를 비인가 방식으로 호출하거나 우회 접근한 경우',
                },
                {
                  number: 'd.',
                  content:
                    '서비스 약관 또는 법령을 위반하여 회사에 손해를 끼친 경우',
                },
              ],
            },
            {
              number: '3.9.2',
              content:
                '회사는 위 항의 조치와 별도로, 손해배상을 청구할 수 있다.',
            },
            {
              number: '3.9.3',
              content:
                '부정사용이 의심되는 경우, 회사는 관련 증빙자료를 확보하여 계정정지 및 법적 대응을 진행할 수 있다.',
            },
          ],
        },
      ],
    },
    {
      title: '4. 데이터 및 지식재산권',
      subsections: [
        {
          title: '4.1 데이터의 정의 및 소유권',
          items: [
            {
              number: '4.1.1',
              content:
                '회사가 제공하는 데이터 및 이에 부수하는 분석 알고리즘, 시각화 도구, 시스템 구성요소 등은 회사의 독자적인 지식재산으로서, 「저작권법」,  「부정경쟁 방지 및 영업비밀 보호에 관한 법률」 등 관련 법률에 의해 보호된다.',
            },
            {
              number: '4.1.2',
              content:
                '회원은 서비스 이용 과정에서 제공되는 데이터에 대한 소유권을 취득하지 않으며, 회사로부터 허락받은 범위 내에서만 이용할 수 있다.',
            },
            {
              number: '4.1.3',
              content:
                '회사가 제공하는 데이터에는 외부 공공기관(예: 한국천문연구원, ESA, NORAD 등)으로부터 제공받은 자료가 포함될 수 있으며, 해당 데이터의 권리는 각 기관에 귀속된다.',
            },
          ],
        },
        {
          title: '4.2 데이터 이용허락 및 제한',
          items: [
            {
              number: '4.2.1',
              content:
                '회사는 회원에게 다음 조건에 따라 데이터의 비독점적이고 양도불가하며 취소 가능한 이용권(license)을 부여한다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '서비스 내 조회, 분석, 시각화 등 플랫폼 기능을 통한 이용',
                },
                {
                  number: 'b.',
                  content: 'API 호출을 통한 한정된 프로그램적 접근',
                },
                {
                  number: 'c.',
                  content:
                    '계약 또는 결제조건에 따라 허용된 범위 내의 다운로드',
                },
              ],
            },
            {
              number: '4.2.2',
              content: '회원은 다음 각 호의 행위를 할 수 없다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '데이터 또는 API 결과를 무단으로 복제, 수정, 배포, 전송, 판매하거나 제3자에게 제공하는 등 상업적으로 이용하는 행위',
                },
                {
                  number: 'b.',
                  content:
                    '데이터를 인공지능(AI) 학습, 모델훈련, 데이터베이스 구축 등 2차적 용도로 사용하는 행위',
                },
                {
                  number: 'c.',
                  content:
                    '데이터 또는 서비스를 이용하여 경쟁 서비스·제품을 개발하거나 영업에 이용하는 행위',
                },
                {
                  number: 'd.',
                  content:
                    '회사의 명시적 허가 없이 데이터의 일부 또는 전체를 공공적으로 게시·출판하는 행위',
                },
                {
                  number: 'e.',
                  content:
                    '데이터 내의 저작권 표시, 상표, 워터마크, 메타데이터를 제거하거나 변경하는 행위',
                },
              ],
            },
            {
              number: '4.2.3',
              content:
                '회사는 API의 안정적인 운영을 위해 회원의 일별·월별 API 호출량, 데이터 요청량, 트래픽 제한을 설정할 수 있으며, 이 경우 회원은 회사가 사전 공지하는 호출 정책, 속도제한(rate limit), 데이터 용량제한 등 기술적 제약조건을 준수해야 한다. 회원이 허용 한도를 초과하여 API를 호출하거나 자동화된 대량요청(bot, crawler 등)을 수행한 경우, 회사는 본 약관에 정한 바에 따라 회원의 서비스 이용을 제한하거나 정지할 수 있다.',
            },
            {
              number: '4.2.4',
              content:
                '회사는 회원이 위 금지행위를 한 경우, 즉시 서비스 이용을 제한하거나 계약을 해지할 수 있으며, 손해배상을 청구할 수 있다.',
            },
            {
              number: '4.2.5',
              content:
                '회원이 데이터 재가공·외부보고 목적의 이용을 원하는 경우, 별도의 데이터 라이선스 계약을 체결해야 한다.',
            },
            {
              number: '4.2.6',
              content:
                '회원은 서비스 이용 중 제공받은 데이터를 제3자와 공유하거나 외부저장소에 업로드해서는 안 되며, 서비스상 가능한 기능을 이용하여 데이터 또는 회원이 생성한 데이터를 제3자와 공유하거나 외부저장소에 업로드 하는 경우에도 본 약관의 다른 제반 의무를 위반하지 않아야 한다.',
            },
            {
              number: '4.2.7',
              content:
                '본 약관에 따라 회원이 데이터에 대하여 보유하는 이용권은 회사와 회원간의 서비스 이용계약이 유효한 기간에 한하여 효력을 가진다. ',
            },
          ],
        },
        {
          title: '4.3 회원이 생성한 데이터에 대한 권리의 귀속 및 회사의 활용',
          items: [
            {
              number: '4.3.1',
              content:
                '회원이 서비스 이용 중 생성한 자료(예: 사용자 입력 궤도데이터, 시뮬레이션 결과, 스크립트, 회원이 데이터를 활용하여 생성한 2차 가공데이터, 파생 분석결과, 시각화 콘텐츠 기타 모든 산출물 등, 이하 “회원생성자료”)는 을의 창작적 기여가 인정되는 한 그러한 범위에서 회원에게 귀속된다. 단, 회원생성자료에 데이터가 포함되거나 가공되어 사용된 경우에는 해당 자료에 대하여 4.2.2.조 내지 4.2.6.조가 적용된다. 명확히 하면, 회원생성자료의 기반이 되는 회사의 데이터는 여전히 회사의 자산이며, 회원생성자료에 회사의 데이터 기타 자료가 포함되거나 가공되어 사용된 경우 회원은 본 약관에서 달리 허용되지 않는 한 회원생성자료를 판매, 양도, 기타의 방법으로 제3자에게 제공할 수 없다.',
            },
            {
              number: '4.3.2',
              content:
                '회원이 회원생성자료를 서비스 내에 게시하는 경우, 회사는 서비스 제공 과정에서 서비스 소개, 홍보, 회원 또는 타회원에 대한 검색 결과 제공, 기타 맞춤형 서비스 제공 등의 목적으로 이를 이용할 수 있다.',
            },
            {
              number: '4.3.3',
              content:
                '회사는 회원이 플랫폼 내에 저장한 회원생성자료를 비식별화하여 서비스 개선 목적으로 이용할 수 있다.',
            },
            {
              number: '4.3.4',
              content:
                '회원은 서비스 내에 게시하는 회원생성자료에 제3자의 저작권, 개인정보, 영업비밀 등이 포함되지 않도록 주의해야 하며, 회원생성자료로 인해 발생한 법적 문제의 책임은 회원에게 있다.',
            },
            {
              number: '4.3.5',
              content:
                '회원은 탈퇴 전 본인이 업로드한 회원생성자료 등의 백업을 스스로 수행해야 하며, 탈퇴 후에는 이를 다운로드 할 수 없다.',
            },
            {
              number: '4.3.6',
              content: '본 조는 서비스 이용계약의 해지 이후로도 유효하다.',
            },
          ],
        },
        {
          title: '4.4 제3자 데이터 및 외부 소스',
          items: [
            {
              number: '4.4.1',
              content:
                '회사는 서비스 내 일부 데이터가 제3자로부터 제공받은 것임을 명시하고, 회원은 해당 데이터(이하 ‘제3자데이터’)의 라이선스 조건을 준수해야 한다.',
            },
            {
              number: '4.4.2',
              content:
                '제3자 데이터는 공공데이터(Open Data)일 수 있으나, 회사의 재가공·가시화 작업이 포함된 경우 회사의 2차적저작권 내지 데이터베이스제작자의 권리가 인정된다.',
            },
            {
              number: '4.4.3',
              content:
                '회원은 제3자데이터의 권리자가 부여한 이용범위를 넘어서는 행위를 할 수 없다.',
            },
          ],
        },
        {
          title: '4.5 지식재산권의 귀속',
          items: [
            {
              number: '4.5.1',
              content:
                '서비스 및 데이터와 관련된 모든 권리(저작권, 상표권, 디자인권, 특허권, 영업비밀 등)는 회사에 귀속된다.',
            },
            {
              number: '4.5.2',
              content:
                '회원은 서비스 이용을 통해 회사의 상표, 로고, 도메인명, UI/UX 디자인 등을 사용할 수 없다.',
            },
            {
              number: '4.5.3',
              content:
                '회사 명칭(Spacemap), 서비스 로고, 시각화 디자인, 데이터 포맷 등은 등록된 상표 또는 회사의 자산으로 보호된다.',
            },
            {
              number: '4.5.4',
              content:
                '제3자의 권리 보호를 위해, 회사는 필요 시 특정 데이터의 제공을 중단하거나 수정할 수 있다.',
            },
          ],
        },
        {
          title: '4.6 침해 신고 및 조치',
          items: [
            {
              number: '4.6.1',
              content:
                '회원이 본 서비스의 데이터, 콘텐츠, API 등을 무단 사용하거나 지식재산권을 침해했다고 판단되는 경우, 회사는 즉시 조사 및 조치를 취할 수 있다.',
            },
            {
              number: '4.6.2',
              content:
                '회사는 침해행위가 확인되면 해당 계정을 정지하고, 법적 절차를 진행할 수 있다.',
            },
            {
              number: '4.6.3',
              content:
                '회원은 회사의 데이터 또는 서비스를 침해하거나 불법적으로 재배포한 경우, 민형사상 책임을 진다.',
            },
          ],
        },
        {
          title: '4.7 연구 및 공익목적 활용',
          items: [
            {
              number: '4.7.1',
              content:
                '회사는 국가기관, 학계, 국제기구 등 공공목적의 연구 또는 안전보장 관련 요청이 있는 경우, 합법적 절차에 따라 데이터를 제한적 범위 내에서 재량으로 추가 제공할 수 있다.',
            },
            {
              number: '4.7.2',
              content:
                '전항의 경우 회사는 데이터를 추가적으로 제공받는 회원과 데이터의 비상업적 목적, 기밀유지 조건, 기술적 접근제한 등을 포함한 별도 계약을 체결할 수 있다.',
            },
          ],
        },
        {
          title: '4.8 데이터 출처 표시 및 저작권 고지',
          items: [
            {
              number: '4.8.1',
              content:
                '본 약관의 다른 조항에도 불구하고, 회원은 비상업적인 연구∙학술 목적에 한하여 회사의 정당한 이익을 부당하게 해치지 아니하는 범위 내에서 회사의 데이터 및 회원생성자료를 연구, 보고서 등에 활용할 수 있다. 단, 이 경우 반드시 “자료출처: Spacemap Platform (© SPACEMAP, All rights reserved. www.spacemap42.com)” 또는 회사가 지정한 형태로 출처를 명시해야 한다.',
            },
            {
              number: '4.8.2',
              content:
                '출처 표시를 누락하거나 데이터의 출처를 오인하게 하는 행위는 저작권 침해로 간주될 수 있다.',
            },
          ],
        },
      ],
    },
    {
      title: '5. 보안 및 규제 준수',
      subsections: [
        {
          title: '5.1 데이터 보안관리',
          items: [
            {
              number: '5.1.1',
              content:
                '회사는 보안사고 예방을 위하여 정기적인 취약점 점검 및 침투테스트를 실시할 수 있다.',
            },
            {
              number: '5.1.2',
              content:
                '회사는 개인정보 보호법 등 관련 법령에 따라 개인정보 및 민감데이터를 보호한다.',
            },
          ],
        },
        {
          title: '5.2 접근통제 및 인증',
          items: [
            {
              number: '5.2.1',
              content:
                '회사는 위성데이터 및 API 응답에 대한 무단 수집·스크래핑 행위를 방지하기 위해 다음과 같은 기술적 조치를 시행할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'API 호출 제한(rate limiting) 및 인증토큰 만료시간 설정',
                },
                {
                  number: 'b.',
                  content: '비정상 요청 탐지 및 차단 시스템(WAF, IDS/IPS) 운영',
                },
                {
                  number: 'c.',
                  content: '크롤링·봇 탐지 알고리즘 적용',
                },
                {
                  number: 'd.',
                  content:
                    '데이터 유출 방지를 위한 워터마킹(Watermarking) 또는 접근로그 추적',
                },
              ],
            },
            {
              number: '5.2.2',
              content:
                '회원은 접근권한이 부여된 범위 내에서만 서비스를 이용할 수 있으며, 시스템 또는 API를 우회하거나 비인가 방식으로 접근해서는 안 된다.',
            },
            {
              number: '5.2.3',
              content:
                '회사는 보안위협이 감지된 계정에 대해 일시적으로 접근을 제한하거나 인증절차를 강화할 수 있다.',
            },
            {
              number: '5.2.4',
              content:
                '회사는 보안 강화를 위해 서비스 접근방식을 변경하거나 인증 절차를 추가할 수 있으며, 이에 따라 발생한 불편에 대해서는 보상 의무를 부담하지 않는다.',
            },
          ],
        },
        {
          title: '5.3 국가안보 및 수출통제',
          items: [
            {
              number: '5.3.1',
              content:
                '회사는 우주데이터 및 위성정보의 특성상, 국가안보 및 국제 수출통제 관련 법령(ITAR, EAR 등)을 준수한다.',
            },
            {
              number: '5.3.2',
              content:
                '다음 각 호의 경우, 회사는 회원의 서비스 이용 또는 데이터 접근을 제한하거나 계약을 해지할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '수출통제 대상국가 또는 제재기관(UN, OFAC 등)에 속한 회원인 경우',
                },
                {
                  number: 'b.',
                  content:
                    '이용 목적이 군사·무기체계 개발 등 전략물자 활용과 관련된 경우',
                },
                {
                  number: 'c.',
                  content:
                    '회사가 보유한 데이터가 외교·안보상 민감한 영역으로 판단되는 경우',
                },
              ],
            },
            {
              number: '5.3.3',
              content:
                '회사는 수출통제, 기타 규제 준수를 위해 회원의 소속, 국가, 사용목적에 대한 추가 정보를 요청할 수 있고, 해당 정보가 제공된 후 관련 법령에 위배됨이 없는 경우 비로소 관련된 서비스를 제공할 수 있다. 회사는 관련 기관의 요청에 따라 법적 절차 내에서 회원 정보를 제공할 수 있다.',
            },
          ],
        },
        {
          title: '5.4 정보보호 의무',
          items: [
            {
              number: '5.4.1',
              content:
                '회사는 개인정보 및 서비스 이용정보를 보호하기 위하여 개인정보처리방침을 별도로 수립·공개한다.',
            },
            {
              number: '5.4.2',
              content:
                '회사는 최소한의 개인정보만을 수집하며, 서비스 운영 목적 외의 용도로 사용하지 않는다.',
            },
            {
              number: '5.4.3',
              content:
                '회원은 회사의 개인정보 보호정책을 숙지하고, 자신의 연락처 등 정보를 최신 상태로 유지해야 하며, 정보 미갱신으로 인한 불이익에 대해 회사는 책임을 지지 않는다.',
            },
            {
              number: '5.4.4',
              content:
                '회원은 제3자의 개인정보를 무단으로 수집·전송·게시할 수 없으며, 위반 시 관련 법령에 따라 책임을 진다.',
            },
          ],
        },
      ],
    },
    {
      title: '6. 데이터 관련 면책',
      subsections: [
        {
          title: '6.1 데이터 본질 및 한계',
          items: [
            {
              number: '6.1.1',
              content:
                '회원은 회사가 제공하는 모든 데이터, 분석결과, 시뮬레이션, 예측정보 등은 과학적·수학적 모델, 외부 관측자료, 공공 데이터셋 등을 기반으로 산출된 추정치이며, 실제 현상과 다를 수 있음을 확인한다.',
            },
            {
              number: '6.1.2',
              content:
                '회원은 위성궤도, 충돌확률, 우주상황인식(SSA) 정보 등이 시간·좌표계·센서정확도·모델링오차 등 다양한 요인에 의해 불확실성을 내포함을 확인한다.',
            },
            {
              number: '6.1.3',
              content:
                '회사는 데이터의 절대적 정확성, 완전성, 시의성 또는 지속적 일관성을 보장하지 않는다.',
            },
            {
              number: '6.1.4',
              content:
                '회원은 이러한 데이터가 “참고자료(reference data)”로 제공됨을 이해하고, 위성운용, 발사계획, 보험계약 등 의사결정 또는 사업활동의 결과에 대해 전적인 책임을 진다.',
            },
          ],
        },
        {
          title: '6.2 데이터 해석 및 이용에 대한 비의무',
          items: [
            {
              number: '6.2.1',
              content:
                '회사는 회원이 데이터를 해석하거나 응용하는 과정에서 발생한 판단·결정·조치에 대해 책임을 지지 않는다.',
            },
            {
              number: '6.2.2',
              content:
                '회사는 데이터 해석 또는 분석결과의 의미를 개별적으로 설명하거나, 특정 목적(예: 발사일정, 충돌회피기동, 보험심사 등)에 적합하다는 보증을 제공하지 않는다.',
            },
            {
              number: '6.2.3',
              content:
                '회사가 제공하는 모든 분석자료는 “정보 제공 목적”으로 한정되며, 계약상 또는 법적 조언(advisory)으로 간주되지 않는다.',
            },
            {
              number: '6.2.4',
              content:
                '회원이 본 약관에 동의함으로써, 회사는 데이터의 사용 또는 해석으로 인한 결과에 대한 법적 책임에서 면책된다.',
            },
          ],
        },
        {
          title: '6.3 데이터 제공 중단 및 변경',
          items: [
            {
              number: '6.3.1',
              content:
                '회사는 다음 각 호의 사유가 있는 경우, 데이터의 제공을 일시 중단하거나 변경할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content: '데이터 출처 기관의 정책 변경 또는 서비스 중단',
                },
                {
                  number: 'b.',
                  content: '기술적 오류, 데이터 손상, 품질 저하 발생',
                },
                {
                  number: 'c.',
                  content: '수출통제, 국가안보, 외교적 제약 등 법적 사유',
                },
                {
                  number: 'd.',
                  content:
                    '회사의 자체 분석 알고리즘 개선 또는 업데이트 필요 시',
                },
              ],
            },
            {
              number: '6.3.2',
              content:
                '회사는 데이터 제공이 중단되거나 변경될 경우, 가능한 한 사전에 공지한다.',
            },
            {
              number: '6.3.3',
              content:
                '회사는 위 사유로 인한 데이터 이용 제한에 대하여, 고의 또는 중대한 과실이 없는 한 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '6.4 외부기관 데이터의 신뢰성',
          items: [
            {
              number: '6.4.1',
              content:
                '회사는 외부기관(예: ESA, NORAD, KARI, JAXA, Space-Track 등)으로부터 제공받은 데이터를 가공·분석하여 제공할 수 있다.',
            },
            {
              number: '6.4.2',
              content:
                '외부기관의 데이터는 제공시점의 상태를 반영한 것이며, 이후 변경되거나 수정될 수 있다.',
            },
            {
              number: '6.4.3',
              content:
                '회사는 외부기관 데이터의 정확성, 완전성 또는 최신성에 대해 어떠한 보증도 하지 않으며, 이에 대하여 책임지지 않는다.',
            },
            {
              number: '6.4.4',
              content:
                '회원은 외부기관의 데이터가 포함된 결과를 사용할 경우, 그 출처를 명시해야 하며, 외부기관의 이용조건을 준수해야 한다.',
            },
          ],
        },
        {
          title: '6.5 모델링 및 예측결과의 불확실성',
          items: [
            {
              number: '6.5.1',
              content:
                '회사의 예측 알고리즘, 충돌확률 모델, 궤도전파 계산 등은 확률적(statistical)·수치적 모델에 기반하며, 실제 관측 값 또는 궤도환경 변화에 따라 오차가 발생할 수 있다.',
            },
            {
              number: '6.5.2',
              content:
                '충돌확률 예측, 궤도접근(Conjunction) 계산, 궤적 시뮬레이션 결과 등은 단지 위험 수준의 정량적 지표일 뿐, 실제 충돌·비행·운용상태를 확정적으로 판단하는 근거로 사용되어서는 안 된다.',
            },
            {
              number: '6.5.3',
              content:
                '회사는 모델의 불확실성, 입력데이터의 불완전성, 수학적 근사오류 기타 외부 변수로 인해 발생한 손해에 대해 책임을 지지 않는다.',
            },
            {
              number: '6.5.4',
              content:
                '회사는 예측결과를 주기적으로 개선·보정하기 위해 알고리즘 업데이트를 수행할 수 있다.',
            },
          ],
        },
      ],
    },
    {
      title: '7. 회원 및 회사의 의무',
      subsections: [
        {
          title: '7.1 회원의 기본의무',
          items: [
            {
              number: '7.1.1',
              content:
                '회원은 본 약관, 관련 법령, 회사의 공지 및 이용정책을 준수해야 한다.',
            },
            {
              number: '7.1.2',
              content:
                '회원은 서비스 이용 과정에서 다음 각 호의 행위를 해서는 안 된다.',
              subItems: [
                {
                  number: 'a.',
                  content: '허위 정보 등록 또는 타인의 정보 도용',
                },
                {
                  number: 'b.',
                  content:
                    '시스템 해킹, 비인가 접근, API 우회 호출 등 서비스 침해 행위',
                },
                {
                  number: 'c.',
                  content:
                    '데이터의 구조, 알고리즘 또는 코드의 역분석(reverse engineering), 디컴파일, 추출',
                },
                {
                  number: 'd.',
                  content:
                    '서비스의 안정적 운영을 저해하는 행위 (자동화 스크래핑, 과도한 요청 등)',
                },
                {
                  number: 'e.',
                  content: '데이터의 무단 복제·가공·판매·전송 또는 외부 공개',
                },
                {
                  number: 'f.',
                  content:
                    'AI 학습, 데이터베이스 구축 등 2차적 활용 행위 (사전 승인 없는 경우)',
                },
                {
                  number: 'g.',
                  content: '회사 또는 제3자의 지식재산권 침해',
                },
                {
                  number: 'h.',
                  content:
                    '서비스를 이용해 획득한 정보를 불법적, 비윤리적 또는 규제위반 목적으로 사용하는 행위',
                },
                {
                  number: 'i.',
                  content:
                    '제3자의 권리를 침해하거나 허위·왜곡된 정보를 생성하는 행위',
                },
                {
                  number: 'j.',
                  content:
                    '타인의 명예를 훼손하거나 공공질서에 반하는 콘텐츠 게시',
                },
                {
                  number: 'k.',
                  content:
                    '수출통제 위반, 불법적 데이터 이전 또는 국가안보 위반 행위',
                },
              ],
            },
            {
              number: '7.1.3',
              content:
                '회원은 본 서비스가 과학적 모델 기반의 정보제공 서비스임을 이해하고, 서비스 결과를 상업적, 법적 근거로 남용하지 않아야 한다.',
            },
            {
              number: '7.1.4',
              content:
                '회원은 회사의 합리적인 요청(데이터 검증, 신원확인 등)에 성실히 응해야 한다.',
            },
          ],
        },
        {
          title: '7.2 회원의 정보관리 의무',
          items: [
            {
              number: '7.2.1',
              content:
                '회원은 자신의 계정정보, API 키, 비밀번호, 인증토큰 등을 안전하게 관리해야 하며, 제3자에게 공유(GitHub, Notion 등 공개된 외부 저장소에의 업로드를 포함함)하거나 양도할 수 없다.',
            },
            {
              number: '7.2.2',
              content:
                '회원은 계정 도용 또는 비정상 접근이 발생한 경우 혹은 API 키나 인증토큰이 분실, 도난 또는 유출된 경우 즉시 회사에 통지해야 하며, 회사의 안내에 따라 조치를 취해야 한다.',
            },
            {
              number: '7.2.3',
              content:
                '기업회원은 소속 사용자(Sub-user)들의 계정 관리 및 이용기록에 대해 전적인 책임을 진다.',
            },
            {
              number: '7.2.4',
              content:
                '회사는 회원의 과실로 인한 정보유출, 계정 침해 등 보안 사고에 대해서는 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '7.3 회원의 기술적 협력 의무',
          items: [
            {
              number: '7.3.1',
              content:
                '회사가 서비스 품질 개선, 시스템 점검, 보안위협 대응 등을 위해 협력을 요청하는 경우, 회원은 합리적인 범위 내에서 이에 협조해야 한다.',
            },
            {
              number: '7.3.2',
              content:
                'API 또는 시스템 연동을 사용하는 기업회원은, 회사가 지정한 기술규격 및 호출제한을 준수해야 한다.',
            },
            {
              number: '7.3.3',
              content:
                '서비스 접근은 HTTPS, 인증토큰, API 키 등 회사가 정한 방식으로만 가능하며, 비인가 API 호출, 자동화된 데이터 수집, 비공식 SDK 사용 등을 해서는 안 된다.',
            },
            {
              number: '7.3.4',
              content:
                '회사는 기술적 협력 불이행으로 인해 서비스 오류가 발생한 경우 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '7.4 회원의 법적 준수 의무',
          items: [
            {
              number: '7.4.1',
              content:
                '회원은 서비스 이용과 관련하여 데이터 사용 시 「우주개발진흥법」, 「위성정보 활용촉진 및 보호법」, 「정보통신망이용 촉진 및 정보 보호에 관한 법률」, ITAR, EAR 및 회원이 적용을 받는 국가의 법령, 국제조약, 행정규제 등을 준수해야 한다.',
            },
            {
              number: '7.4.2',
              content: '특히 다음 각 호의 행위는 엄격히 금지된다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '위성궤도·충돌예측 데이터 등 우주데이터의 군사적 전용 또는 제3국 이전, 재판매(ITAR/EAR 위반 행위 포함)',
                },
                {
                  number: 'b.',
                  content: '데이터의 해외 전송 시 암호화 및 접근제한 미준수',
                },
                {
                  number: 'c.',
                  content: '위성운용, 발사체 운용 관련 허가 없는 정보 활용',
                },
                {
                  number: 'd.',
                  content:
                    '제3자의 개인식별정보(PII)를 포함한 데이터의 무단 업로드',
                },
              ],
            },
            {
              number: '7.4.3',
              content:
                '회원은 회사가 제공하는 보안지침을 숙지하고, 보안규정을 위반하지 않도록 노력해야 한다.',
            },
            {
              number: '7.4.4',
              content:
                '법령 또는 회사가 제공한 보안규정 위반이 확인될 경우, 회사는 회원의 서비스 이용을 즉시 중단하고 법적 절차를 개시할 수 있다.',
            },
            {
              number: '7.4.5',
              content:
                '회원은 본 조의 위반으로 인해 발생한 제3자 클레임, 기타 민·형사, 행정상 조치에 대해 회사를 면책하고, 회사에게 발생한 손해를 배상하여야 한다.',
            },
          ],
        },
        {
          title: '7.5 회사의 기본의무',
          items: [
            {
              number: '7.5.1',
              content:
                '회사는 관련 법령 및 본 약관에 따라 안정적이고 지속적인 서비스를 제공하기 위해 최선을 다한다.',
            },
            {
              number: '7.5.2',
              content:
                '회사는 서비스의 보안성, 접근 안정성을 확보하기 위한 기술적·관리적 조치를 시행한다.',
            },
            {
              number: '7.5.3',
              content:
                '회사는 회원의 데이터를 본 약관 및 개인정보처리방침에서 정한 범위 내에서만 이용한다.',
            },
            {
              number: '7.5.4',
              content:
                '회사는 회원에게 불리한 변경, 유료정책 조정, 서비스 중단 등이 있을 경우 사전에 이를 고지한다.',
            },
            {
              number: '7.5.5',
              content:
                '회사는 회원의 문의·신고·요청에 대해 신속하고 성실하게 응답한다.',
            },
            {
              number: '7.5.6',
              content:
                '회사는 서비스 안정성과 데이터 보호를 위하여, 필요 시 특정 국가 또는 네트워크에서의 접근을 제한할 수 있다.',
            },
          ],
        },
        {
          title: '7.6 서비스 중단',
          items: [
            {
              number: '7.6.1',
              content:
                '회사는 다음 각 호의 사유가 발생한 경우, 서비스의 전부 또는 일부를 일시적으로 중단할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content: '시스템 점검, 유지보수, 업그레이드 등 기술적 필요',
                },
                {
                  number: 'b.',
                  content:
                    '데이터센터 장애, 네트워크 문제, 천재지변 등 불가항력적 사유',
                },
                {
                  number: 'c.',
                  content: '정부 명령, 수출통제, 국가안보 관련 요청',
                },
                {
                  number: 'd.',
                  content: '외부기관(데이터 제공자)의 정책변경 또는 API 제한',
                },
              ],
            },
            {
              number: '7.6.2',
              content:
                '회사는 계획된 중단의 경우 최소 24시간 이전에 공지하며, 긴급 중단 시 사후 공지를 통해 사유 및 조치계획을 안내한다.',
            },
            {
              number: '7.6.3',
              content:
                '서비스 중단으로 인한 회원의 손해에 대해, 회사는 고의 또는 중대한 과실이 없는 한 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '7.7 제휴 및 협력관계',
          items: [
            {
              number: '7.7.1',
              content:
                '회사는 서비스 운영을 위해 제휴사, 연구기관, 정부기관 등과 협력할 수 있으며, 이 경우 회원 데이터는 비식별화된 형태로만 제공된다.',
            },
            {
              number: '7.7.2',
              content:
                '제휴 서비스의 이용 시, 해당 기관의 정책 또는 약관이 추가로 적용될 수 있다.',
            },
            {
              number: '7.7.3',
              content:
                '회사는 제휴 관계를 통해 신규 서비스를 제공하는 경우, 사전에 공지한다.',
            },
          ],
        },
        {
          title: '7.8 게시물의 삭제 등',
          items: [
            {
              number: '7.8.1',
              content:
                '회원이 게시한 게시물(회원이 서비스 상에 게시한 부호, 문자, 그림, 사진, 영상, 음성, 음향, 각종 파일, 콘텐츠를 포함하며 그 형태를 불문합니다) 기타 서비스로 인하여 사생활 침해, 명예훼손, 저작권 등 다른 회원 또는 제3자의 권리가 침해된 경우 그 침해를 받은 자는 회사에 침해사실을 소명하여 그 게시물의 삭제 또는 반박내용의 게재(이하 “삭제등”)를 요청할 수 있다.',
            },
            {
              number: '7.8.2',
              content:
                '회사는 해당 게시물의 삭제등을 요청받으면 지체 없이 삭제, 임시 게시중단, 수정, 이동, 등록거부 등 필요한 조치를 취하고 즉시 신청인 및 게시물 등록자에게 알린다. 회사는 관련 법령에 의하여 요구되는 경우 필요한 조치를 한 사실을 해당 게시판에 공시하는 등의 방법으로 서비스의 회원이 알 수 있도록 한다.',
            },
            {
              number: '7.8.3',
              content:
                '회사는 게시물의 삭제요청에도 불구하고 권리의 침해 여부를 판단하기 어렵거나 이해당사자 간에 다툼이 예상되는 경우에는 30일 이내의 기간을 정하여 해당 정보에 대한 접근을 임시적으로 차단하는 조치(이하 “임시조치”)를 할 수 있다. 삭제등 신청인과 게시물 등록자 간에 소송, 합의 기타 이에 준하는 관련기관의 결정 등이 이루어져 회사에 접수된 경우 이에 따르기로 한다.',
            },
            {
              number: '7.8.4',
              content:
                '다음 사유에 해당하는 게시물의 경우 회사는 다른 회원 또는 제3자의 삭제등 요청이 없더라도 해당 게시물을 게재한 회원에게 조치 사유 및 이의제기 방법을 사전 통지 후 해당 게시물에 대하여 삭제, 임시 게시중단, 수정, 이동, 등록거부 등의 조치를 취할 수 있다. 단, 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」(이하 “정보통신망법”), 「저작권법」 등 관련 법령에 따라 회사가 사전 통지를 요하지 않고 삭제할 수 있거나 삭제해야 하는 경우에는 사전 통지 없이 조치 후 게시물 등록자에게 지체없이 사후 통보한다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '본 약관에 위배되거나 또는 불법, 음란, 저속하다고 판단되는 게시물 게시나 프로필 사진 등을 사용하는 경우',
                },
                {
                  number: 'b.',
                  content:
                    '다른 회원 또는 제3자를 비방, 모욕하거나 중상모략으로 명예를 훼손한 경우',
                },
                {
                  number: 'c.',
                  content: '회사에서 규정한 게시기간이나 용량을 초과한 경우',
                },
                {
                  number: 'd.',
                  content:
                    '공공질서, 미풍양속에 위반되는 내용을 유포하거나 링크 시킨 경우',
                },
                {
                  number: 'e.',
                  content:
                    '불법복제, 해킹을 조장하는 내용의 게시물을 게재한 경우',
                },
                {
                  number: 'f.',
                  content:
                    '개인정보 유포, 범죄와 결부, 위법한 내용의 게시물을 게재한 경우',
                },
                {
                  number: 'g.',
                  content: '제3자의 저작권 등 지식재산권을 침해한 경우',
                },
                {
                  number: 'h.',
                  content:
                    '사적인 정치적 판단이나 종교적 견해의 내용으로 회사가 서비스 성격에 부합하지 않는다고 판단하는 경우',
                },
                {
                  number: 'i.',
                  content:
                    '영리를 목적으로 하거나 특정 대상의 광고 또는 선전을 하는 경우',
                },
                {
                  number: 'j.',
                  content:
                    '회사 임직원을 포함하여, 타인의 정보를 도용하거나 사칭, 허위사실 유포를 하는 경우',
                },
                {
                  number: 'k.',
                  content:
                    '동일 내지 유사한 내용의 게시물을 연속으로 등록하는 도배 행위를 하는 경우',
                },
                {
                  number: 'l.',
                  content: '서비스 제공 목적에 위배되는 내용을 게재하는 경우',
                },
                {
                  number: 'm.',
                  content: '기타 관련 법령에 위배된다고 판단되는 경우',
                },
              ],
            },
            {
              number: '7.8.5',
              content:
                '전 항에 따라 사전통지 받은 회원은 30일 내 이의신청을 할 수 있으며, 이의신청이 정당한 경우 회사는 게시물의 복구 등 보호조치를 실행한다.',
            },
            {
              number: '7.8.6',
              content:
                '회원이 게시한 게시물 등으로 인하여 본인 또는 타인에게 손해나 기타 문제가 발생하는 경우 회원은 이에 대한 책임을 지게 되며, 회사는 고의 또는 중과실 기타 관련 법령이 정한 책임이 없는 한 이에 대하여 책임을 지지 않는다.',
            },
          ],
        },
      ],
    },
    {
      title: '8. 계약 해지 및 서비스 종료',
      subsections: [
        {
          title: '8.1 회원의 계약해지',
          items: [
            {
              number: '8.1.1',
              content:
                '회원은 언제든지 서비스 내 ‘계정관리’ 기능을 통해 이용계약을 해지할 수 있다.',
            },
            {
              number: '8.1.2',
              content:
                '해지 의사표시는 시스템을 통해 접수된 시점에 효력이 발생하며, 해지 즉시 서비스 이용이 중단된다.',
            },
            {
              number: '8.1.3',
              content:
                '해지 후에는 관련 법령 또는 개인정보처리방침에 달리 규정되지 않는 한 계정과 연계된 모든 데이터, 설정, API 키, 이용기록이 삭제되며 복구되지 않는다.',
            },
          ],
        },
        {
          title: '8.2 회사에 의한 계약해지',
          items: [
            {
              number: '8.2.1',
              content:
                '회사는 다음 각 호의 사유가 발생한 경우, 서비스 이용을 제한하거나 사전 통보 후 이용계약을 해지할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content:
                    '회원이 본 약관을 위반하거나 서비스와 관련하여 법령, 또는 공공질서에 반하는 행위를 한 경우',
                },
                {
                  number: 'b.',
                  content: '서비스 요금이 미납된 경우',
                },
                {
                  number: 'c.',
                  content:
                    '데이터 부정사용, 무단복제, 재판매 행위, 회사가 허용한 범위를 벗어난 서비스 이용 또는 상업적 재가공이 있는 경우',
                },
                {
                  number: 'd.',
                  content:
                    '허위 정보 제공, 타인 명의 도용, 비인가 접근 시도 등이 확인된 경우',
                },
                {
                  number: 'e.',
                  content:
                    '시스템 침해, 해킹 시도, 과도한 API 요청이 있는 경우',
                },
                {
                  number: 'f.',
                  content:
                    '회사의 합리적 판단에 따라 서비스 제공이 더 이상 적절하지 않다고 판단되는 경우',
                },
              ],
            },
            {
              number: '8.2.2',
              content:
                '회원의 현행법 위반 및 고의 또는 중대한 과실로 위 항의 사유가 발생한 경우, 회사는 별도 통보 없이 즉시 계약을 해지할 수 있다. 이 경우 회원은 유료서비스의 사용권한을 상실하고 이로 인한 환불 및 손해배상을 청구할 수 없다.',
            },
            {
              number: '8.2.3',
              content:
                '회사는 계약해지 시 해지사유 및 효력발생일을 회원에게 통지한다.',
            },
            {
              number: '8.2.4',
              content:
                '회사는 이용계약이 해지된 회원에 대해 1년간 서비스 재가입을 제한할 수 있다.',
            },
          ],
        },
        {
          title: '8.3 서비스 종료',
          items: [
            {
              number: '8.3.1',
              content:
                '회사는 경영 악화, 기타 사업적·기술적·법적 사유로 인하여 서비스의 제공을 영구적으로 종료할 수 있다.',
            },
            {
              number: '8.3.2',
              content:
                '서비스 종료 시 회사는 종료 예정일 30일 이전까지 회원에게 다음 사항을 공지한다.',
              subItems: [
                {
                  number: 'a.',
                  content: '서비스 종료일',
                },
                {
                  number: 'b.',
                  content: '잔여 이용요금의 정산 절차',
                },
              ],
            },
            {
              number: '8.3.3',
              content:
                '회사는 서비스 종료 후에도 법적 의무에 따라 일정 기간 데이터 및 거래기록을 보존할 수 있다.',
            },
            {
              number: '8.3.4',
              content:
                '서비스 종료로 인해 발생한 회원의 불이익에 대해, 회사는 고의 또는 중대한 과실이 없는 한 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '8.4 데이터의 처리 및 보존',
          items: [
            {
              number: '8.4.1',
              content:
                '회원은 해지 전 반드시 데이터를 백업해야 하며, 계약 종료 후 데이터 손실에 대하여 회사는 책임을 지지 않는다.',
            },
            {
              number: '8.4.2',
              content:
                '회원의 탈퇴 이전에 작성된 게시물은 탈퇴와 함께 자동으로 서비스 내에서 비공개 처리되며, 회사는 내부 운영 관리 목적을 위해 제한적으로 이를 보관한다.',
            },
          ],
        },
        {
          title: '8.5 환불 및 정산 절차',
          items: [
            {
              number: '8.5.1',
              content:
                '서비스 이용 중 해지 또는 종료된 경우, 환불은 제3.5조에 따른다.',
            },
            {
              number: '8.5.2',
              content:
                '환불이 가능한 경우, 환불금은 결제수단 또는 회사가 지정한 방법을 통해 반환된다.',
            },
            {
              number: '8.5.3',
              content:
                '회사는 환불 요청 접수 후 7영업일 이내에 환불 절차를 완료한다.',
            },
          ],
        },
        {
          title: '8.6 계약해지의 효과',
          items: [
            {
              number: '8.6.1',
              content:
                '계약이 해지된 경우, 회원의 모든 권리와 서비스 이용권한은 즉시 소멸한다.',
            },
            {
              number: '8.6.2',
              content:
                '계약 해지로 인해 회사가 입은 손해가 있을 경우, 회사는 회원에게 손해배상을 청구할 수 있다.',
            },
          ],
        },
        {
          title:
            '8.7 회원은 통지 확인 의무를 가지며, 통지 미확인으로 인한 불이익에 대해 회사는 회사의 고의나 과실이 없는 한 책임지지 않는다.',
          items: [],
        },
      ],
    },
    {
      title: '9. 손해배상 및 면책',
      subsections: [
        {
          title: '9.1 손해배상',
          items: [
            {
              number: '9.1.1',
              content:
                '일방 당사자가 본 약관을 위반하거나 불법행위를 한 경우, 상대방에 발생한 모든 손해를 배상해야 한다.',
            },
          ],
        },
        {
          title: '9.2 손해배상책임과 범위의 한정',
          items: [
            {
              number: '9.2.1',
              content:
                '회사는 합리적인 주의와 기술적 조치를 다한 경우, 회원 또는 제3자가 입은 손해에 대해 추가적인 책임을 부담하지 않는다.',
            },
            {
              number: '9.2.2',
              content:
                '회사의 책임은 관련 법령이 허용하는 한, 고의 또는 중대한 과실이 입증된 경우에 한하여 인정된다.',
            },
          ],
        },
        {
          title: '9.3 시스템 오류 및 서비스 장애 면책',
          items: [
            {
              number: '9.3.1',
              content:
                '회사는 서비스의 제공 과정에서 발생할 수 있는 시스템 장애, 서버 오류, 통신망 장애 등으로 인해 데이터가 지연·누락·중복 및 불가항력(천재지변, 정전, 통신망 단절, 화재, 지진, 전쟁 등)에 의해 서비스가 중단에 대하여 책임을 지지 않는다. 단, 회사의 고의 또는 중대한 과실이 있는 경우에는 그러하지 아니하다.',
            },
            {
              number: '9.3.2',
              content:
                '회사는 시스템 유지보수, 데이터베이스 업그레이드, 알고리즘 갱신 등의 사유로 일시적인 서비스 중단을 시행할 수 있으며, 그로 인한 회원 손해에 대해서는 보상하지 않는다. 단, 회사의 고의 또는 중대한 과실이 있는 경우에는 그러하지 아니하다.',
            },
            {
              number: '9.3.3',
              content:
                '회사는 제3자 클라우드 서비스(AWS, Google Cloud 등) 기반으로 운영되며, 해당 인프라 서비스의 장애로 인해 발생한 데이터 접근 불가에 대해서도 책임을 지지 않는다. 단, 회사의 고의 또는 중대한 과실이 있는 경우에는 그러하지 아니하다.',
            },
          ],
        },
        {
          title: '9.4 회원의 주의의무 및 자기책임',
          items: [
            {
              number: '9.4.1',
              content:
                '회원은 서비스에서 제공하는 데이터의 한계를 숙지하고, 이를 자체 검증·보완하여 사용해야 한다.',
            },
            {
              number: '9.4.2',
              content:
                '회원이 서비스 결과를 발사체 운용, 궤도제어, 충돌회피기동, 정책결정, 보험계약, 위성충돌분쟁 등 고위험 활동의 근거로 사용할 경우, 그 책임은 전적으로 회원에게 있다.',
            },
            {
              number: '9.4.3',
              content:
                '회사는 회원이 데이터를 실제 운영 또는 상업적 판단에 사용함으로써 발생한 손실, 지연, 손해, 제3자 클레임 등에 대해 책임을 지지 않는다.',
            },
            {
              number: '9.4.4',
              content:
                '회원은 데이터의 불확실성을 이해하고, 자체적인 검증·검토 절차를 수행할 의무가 있다.',
            },
            {
              number: '9.4.5',
              content:
                '회사는 회원의 과실, 부주의, 또는 내부 보안규정 위반으로 인한 서비스 장애, 데이터 유출·손실, 계정 침해, 손해 등에 대해서도 책임을 지지 않는다.',
            },
          ],
        },
        {
          title: '9.5 본 조 및 기타 면책조항의 효력',
          items: [
            {
              number: '9.5.1',
              content:
                '제6조를 포함하여 본 계약상 규정된 회사의 면책 규정 및 본 제9조는 서비스 이용계약의 해지 이후로도 유효하다.',
            },
          ],
        },
      ],
    },
    {
      title: '10. 분쟁 해결 및 준거법',
      subsections: [
        {
          title: '10.1 분쟁 예방 및 사전협의',
          items: [
            {
              number: '10.1.1',
              content:
                '회사와 회원은 서비스 이용과 관련하여 발생할 수 있는 문제를 상호 신의성실의 원칙에 따라 원만히 해결하기 위해 노력해야 한다.',
            },
            {
              number: '10.1.2',
              content:
                '회사는 분쟁 예방을 위하여, 기술지원·고객센터·전자적 문의채널을 상시 운영한다.',
            },
          ],
        },
        {
          title: '10.2 관할법원',
          items: [
            {
              number: '10.2.1',
              content:
                '본 약관과 관련하여 소송이 제기되는 경우, 관련 법령에 따라 관할권을 가지는 법원을 관할 법원으로 한다.',
            },
            {
              number: '10.2.2',
              content:
                '전항에도 불구하고 회사와 회원이 체결한 개별계약 등에 관할 법원이 명시된 경우, 해당 법원이 본 약관과 관련된 분쟁의 전속적 합의 관할에 해당한다.',
            },
          ],
        },
        {
          title: '10.3 상호 협력 및 분쟁 예방',
          items: [
            {
              number: '10.3.1',
              content:
                '회사와 회원은 서비스 이용과 관련된 문제 발생 시, 신속하고 협조적인 해결을 위해 상호 협력한다.',
            },
            {
              number: '10.3.2',
              content:
                '상호 간 성실한 협의에도 불구하고 분쟁이 해결되지 않을 경우, 소송은 관련 법령에 따라 관할권을 가지는 법원을 관할 법원으로 한다.',
            },
          ],
        },
        {
          title: '10.4 준거법',
          items: [
            {
              number: '10.4.1',
              content: '본 약관은 대한민국 법령(Korean Law)을 준거법으로 한다.',
            },
            {
              number: '10.4.2',
              content:
                '약관의 해석, 서비스 운영, 계약이행, 책임범위 등 모든 사항은 대한민국 법령 및 상관례를 따른다.',
            },
          ],
        },
      ],
    },
    {
      title: '11. 기타조항',
      subsections: [
        {
          title: '11.1 권리의 양도금지',
          items: [
            {
              number: '11.1.1',
              content:
                '회원은 회사의 사전 서면 동의 없이 본 약관상 권리 또는 의무를 제3자에게 양도, 위임, 담보제공, 대여할 수 없다.',
            },
            {
              number: '11.1.2',
              content:
                '회사는 서비스 운영상 필요에 따라 자회사, 계열사 또는 위탁기관에 회원의 동의 없이 서비스 제공권한을 이전할 수 있으며, 이 경우 해당 사실을 사전에 회원에게 공지한다.',
            },
            {
              number: '11.1.3',
              content:
                '회사의 합병, 분할, 영업양수 등의 사유로 인한 계약관계의 이전은 법령 또는 통지 절차에 따라 유효하게 이루어지며, 별도의 동의를 요하지 않는다.',
            },
          ],
        },
        {
          title: '11.2 약관의 분리 가능성 (Severability)',
          items: [
            {
              number: '11.2.1',
              content:
                '본 약관의 일부 조항이 관할 법원 또는 중재기관에 의해 무효, 위법 또는 집행불가능하다고 판단되더라도, 나머지 조항은 여전히 유효하게 적용된다.',
            },
            {
              number: '11.2.2',
              content:
                '무효로 판단된 조항은 회사의 목적과 회원의 권리를 가장 합리적으로 반영하는 대체 조항으로 해석·적용한다.',
            },
          ],
        },
        {
          title: '11.3 통지 및 연락방식',
          items: [
            {
              number: '11.3.1',
              content:
                '회사가 회원에게 통지를 하는 경우, 다음 각 호 중 하나의 방법으로 수행할 수 있다.',
              subItems: [
                {
                  number: 'a.',
                  content: '서비스 내 공지사항 또는 팝업창으로 게시',
                },
                {
                  number: 'b.',
                  content: '등록된 이메일 주소로 발송',
                },
                {
                  number: 'c.',
                  content: '대시보드 또는 관리자 페이지 내 시스템 알림',
                },
                {
                  number: 'd.',
                  content: '기타 회사가 적절하다고 판단하는 전자적 수단',
                },
              ],
            },
            {
              number: '11.3.2',
              content:
                '회사가 불특정 다수에게 통지할 경우, 서비스 내 공지로 갈음할 수 있다.',
            },
            {
              number: '11.3.3',
              content: '회사의 공지는 플랫폼에 게시된 시점에 효력이 발생한다.',
            },
            {
              number: '11.3.4',
              content:
                '회원의 연락처 변경으로 인해 통지를 수신하지 못한 경우, 회사는 적법한 통지를 이행한 것으로 본다.',
            },
          ],
        },
      ],
    },
    {
      title: '[부칙]',
      subsections: [
        {
          title: '',
          items: [
            {
              number: '',
              content: '본 약관은 2026-01-20 로부터 시행한다.',
            },
          ],
        },
      ],
    },
  ],
};

export const sagaPricingContent: PricingContent = {
  title: 'PRICING POLICY',
  sections: [
    {
      title: '1. Purpose and Scope',
      subsections: [
        {
          title: '1.1 Purpose',
          items: [
            {
              number: '1.1.1',
              content:
                'This Pricing Policy ("Policy") establishes the pricing structure, feature availability, and subscription and billing requirements for the Spacemap SAGA service ("Service"). The objective of this Policy is to ensure a transparent and fair service environment between the Company and the User, and to provide standards that comply with relevant educational and commercial regulations.',
            },
          ],
        },
        {
          title: '1.2 Legal Basis',
          items: [
            {
              number: '1.2.1',
              content:
                'This Policy constitutes Annex C of the B2B Customer Agreement and forms an integral part of the contract. In the event of any inconsistency between this Policy and the Customer Agreement, the provisions of the Customer Agreement shall prevail.',
            },
          ],
        },
        {
          title: '1.3 Scope of Application',
          items: [
            {
              number: '1.3.1',
              content:
                'This Policy applies to all users of the Spacemap SAGA service, including Basic, Standard subscription holders. Pricing and feature policies for other services (such as Spacemap Platform and Tube & Talk) are governed by their respective policies.',
            },
          ],
        },
        {
          title: '1.4 Policy Management',
          items: [
            {
              number: '1.4.1',
              content:
                'The Company may revise this Policy in accordance with the document control and amendment procedures set forth in the Security & Compliance Policy, and any changes will be communicated to Users through the official Spacemap portal or via registered email.',
            },
          ],
        },
      ],
    },
    {
      title: '2. Service Composition and Definitions',
      subsections: [
        {
          title: '2.1 Overview',
          items: [
            {
              number: '2.1.1',
              content:
                'This Chapter defines the functional components, account types, subscription units, and key terminology used within the Spacemap SAGA service. SAGA is a practice-based learning service designed for educational and training purposes, providing structured learning content and interactive simulation environments.',
            },
          ],
        },
        {
          title: '2.2 SAGA Service Components',
          items: [
            {
              number: '2.2.1',
              content:
                'The SAGA service consists of the following three core functional components:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Tutorial: Provides step-by-step educational content including fundamental concepts, analytical procedures, and guided learning modules.',
                },
                {
                  number: 'b.',
                  content:
                    'Arena: Offers mission-based activities, scenario-driven exercises, and problem-solving environments that enable experiential and interactive   learning.',
                },
              ],
            },
          ],
        },
        {
          title: '2.3 Account and Subscription Types',
          items: [
            {
              number: '2.3.1',
              content:
                'The subscription types and account definitions used in this Policy are as follows:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Basic Account: A no-cost account with access to limited features.',
                },
                {
                  number: 'b.',
                  content:
                    'Standard Subscription: A single-user paid subscription intended for individual learners.',
                },
              ],
            },
          ],
        },
        {
          title: '2.4 Definition of Feature Availability Levels',
          items: [
            {
              number: '2.4.1',
              content:
                'The availability of each feature in this Policy is defined as follows:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Available (O): Fully accessible without restrictions.',
                },
                {
                  number: 'b.',
                  content:
                    'Limited (Δ): Provided with partial or reduced functionality.',
                },
                {
                  number: 'c.',
                  content:
                    'Not Available (X): Not provided under the applicable subscription.',
                },
              ],
            },
          ],
        },
        {
          title: '2.5 Application of Higher-Level Definitions',
          items: [
            {
              number: '2.5.1',
              content:
                'Terms or service concepts not explicitly defined in this Policy shall follow the definitions set forth in the Integrated Terms of Service and the B2B Customer Agreement. In the event of any inconsistency, the definitions in the higher-level documents shall prevail.',
            },
          ],
        },
      ],
    },
    {
      title: '3. Pricing Structure and Service Plans',
      subsections: [
        {
          title: '3.1 Purpose',
          items: [
            {
              number: '3.1.1',
              content:
                "The purpose of this Chapter is to define the subscription plans, feature availability, and usage conditions of the Spacemap SAGA service, enabling Users to select the plan most suitable for their educational or learning objectives. SAGA's subscription plans are designed to support a wide range of educational environments, from individual learners to course-level and institution-level users.",
            },
          ],
        },
        {
          title: '3.2 Overview of Subscription',
          items: [
            {
              number: '3.2.1',
              content:
                'The SAGA service offers the following subscription plans:',
              table: {
                headers: ['Plan', 'Pricing', 'Description'],
                rows: [
                  {
                    cells: [
                      {
                        content: 'Basic',
                      },
                      {
                        content: '-',
                      },
                      {
                        content:
                          'A no-cost tier that allows Users to experience a limited set of core learning features.',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: 'Standard',
                      },
                      {
                        content: 'USD 9.99/month Or 99.99/year',
                      },
                      {
                        content:
                          'A personal subscription providing full access to Tutorial features and partial access to Arena features.',
                      },
                    ],
                  },
                ],
              },
            },
          ],
        },
        {
          title: '3.3 Feature Availability by Plan',
          items: [
            {
              number: '3.3.1',
              content:
                'Feature availability for each subscription tier is as follows:',
              table: {
                headers: ['Plan', 'Basic', 'Standard'],
                rows: [
                  {
                    cells: [
                      {
                        content: 'Feature',
                      },
                      {
                        content: 'Tutorial Chapters 1 & 2',
                      },
                      {
                        content: 'All in Basic',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: 'Arena',
                      },
                      {
                        content: 'All Tutorial Chapters',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: 'One Avatar Lunar Explorer',
                      },
                      {
                        content: 'Arena',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: 'One Avatar Satellite',
                      },
                      {
                        content: 'Up to 2 Avatar Lunar Explorers',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: 'Imaging Mission for Each Sat (Max 3 / Month)',
                      },
                      {
                        content: 'Up to 3 Avatar Satellites',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: '',
                      },
                      {
                        content:
                          'Imaging Mission for Each Sat (Max 30 / Month)',
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: '',
                      },
                      {
                        content: '',
                      },
                      {
                        content: 'Conjunction Alerts with Real Satellites',
                      },
                    ],
                  },
                ],
              },
            },
          ],
        },
        {
          title: '3.4 Pricing and Licensing Conditions',
          items: [
            {
              number: '3.4.1',
              content:
                'All subscription fees are calculated on a monthly basis in USD.',
            },
            {
              number: '3.4.2',
              content:
                'The feature availability of the Standard subscriptions may be updated according to SAGA service revisions, and significant changes will be announced in advance.',
            },
          ],
        },
        {
          title: '3.5 Feature Updates and Access Policy',
          items: [
            {
              number: '3.5.1',
              content:
                'SAGA features may be updated to improve service quality, expand educational content, or comply with external regulatory requirements.',
            },
            {
              number: '3.5.2',
              content:
                'Major updates will be announced in advance in accordance with the Product Update Policy.',
            },
            {
              number: '3.5.3',
              content:
                'New features may initially be released in Beta form to ensure service stability.',
            },
          ],
        },
      ],
    },
    {
      title: '4. Payment and Subscription Terms',
      subsections: [
        {
          title: '4.1 Purpose',
          items: [
            {
              number: '4.1.1',
              content:
                'The purpose of this Chapter is to define the payment methods, subscription terms, billing procedures, and cancellation rules applicable to the Spacemap SAGA service, ensuring a clear contractual relationship between the Company and the User and supporting consistent service operations.',
            },
          ],
        },
        {
          title: '4.2 Payment Methods',
          items: [
            {
              number: '4.2.1',
              content:
                'Paid subscriptions for the SAGA service may be settled through the following electronic payment methods:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'PayPal: Available for both individual and institutional Users. Supports international transactions via PayPal accounts.',
                },
              ],
            },
            {
              number: '4.2.2',
              content:
                "All payments are processed securely through the Company's certified payment gateway. The Company does not store customer card or payment information directly (in compliance with PCI-DSS and related security standards).",
            },
            {
              number: '4.2.3',
              content:
                'The Company may introduce additional payment methods to enhance service convenience, and any new payment method will be announced in advance.',
            },
          ],
        },
        {
          title: '4.3 Subscription and Billing Cycle',
          items: [
            {
              number: '4.3.1',
              content:
                'All paid SAGA subscription plans operate on a monthly billing cycle with automatic renewal.',
            },
            {
              number: '4.3.2',
              content:
                'Subscriptions automatically renew unless the User submits a cancellation request.',
            },
            {
              number: '4.3.3',
              content:
                'Receipts or invoices are issued to the User via the designated email or customer portal upon successful payment.',
            },
          ],
        },
        {
          title: '4.4 Taxes and Additional Charges',
          items: [
            {
              number: '4.4.1',
              content: 'All subscription fees are calculated in USD.',
            },
            {
              number: '4.4.2',
              content:
                "Depending on the User's country or payment location, additional taxes such as VAT, OCT, or other statutory charges may apply.",
            },
            {
              number: '4.4.3',
              content:
                'Additional charges may be incurred under the following circumstances, and the Company will notify the User in advance:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Requests for institution-specific features or additional content',
                },
                {
                  number: 'b.',
                  content:
                    'Premium support services, customization, or specialized educational configurations',
                },
              ],
            },
          ],
        },
        {
          title: '4.5 Cancellation and Refund Policy',
          items: [
            {
              number: '4.5.1',
              content:
                'Users may cancel their subscription at any time, and no further charges will be applied from the next billing cycle.',
            },
            {
              number: '4.5.2',
              content:
                'Fees already paid are generally non-refundable. However, refunds or credits may be granted in the following cases:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'A major service outage or prolonged unavailability attributable to the Company',
                },
                {
                  number: 'b.',
                  content:
                    'Duplicate charges or clear payment errors caused by the Company',
                },
              ],
            },
            {
              number: '4.5.3',
              content:
                'Cancellation requests must be submitted through the customer portal or via the designated customer support channel.',
            },
          ],
        },
        {
          title: '4.6 Price Changes and Notices',
          items: [
            {
              number: '4.6.1',
              content:
                'The Company may adjust SAGA subscription fees, if necessary, due to service improvements, increased operational costs, or changes in external data or infrastructure expenses.',
            },
            {
              number: '4.6.2',
              content:
                "If a change is likely to adversely affect the User, the Company will provide at least 30 days' prior notice.",
            },
            {
              number: '4.6.3',
              content:
                'Revised pricing will apply from the next billing cycle following the notice.',
            },
          ],
        },
        {
          title: '4.7 Payment Failures and Account Restrictions',
          items: [
            {
              number: '4.7.1',
              content:
                'In the event of a payment failure, the Company will provide a 7-day grace period for payment retries.',
            },
            {
              number: '4.7.2',
              content:
                'If payment is not completed within the grace period, the account will be placed in a Suspended state.',
            },
            {
              number: '4.7.3',
              content:
                'Repeated payment failures or abnormal payment patterns may result in account restrictions or suspension of service access.',
            },
          ],
        },
      ],
    },
    {
      title: '5. Refund and Cancellation Policy',
      subsections: [
        {
          title: '5.1 Purpose',
          items: [
            {
              number: '5.1.1',
              content:
                'The purpose of this Chapter is to establish detailed rules regarding payments, refunds, subscription cancellations, and credit adjustments for the Spacemap SAGA service. This is intended to clarify the rights and obligations of the Company and the User and prevent unnecessary disputes.',
            },
            {
              number: '5.1.2',
              content:
                'This Chapter supplements Chapter 6 (Fees and Refunds) of the Integrated Terms of Service, and in case of inconsistency, the provisions of the higher-level agreement shall prevail.',
            },
          ],
        },
        {
          title: '5.2 Principle of Non-Refundability',
          items: [
            {
              number: '5.2.1',
              content:
                'All paid subscription plans operate on a prepaid basis, and fees already paid are non-refundable, regardless of mid-term cancellation or usage level.',
            },
            {
              number: '5.2.2',
              content:
                'The following situations do not constitute valid grounds for a refund:',
              subItems: [
                {
                  number: 'a.',
                  content: "User's change of mind or insufficient usage",
                },
                {
                  number: 'b.',
                  content: 'Misunderstanding or misuse of features',
                },
                {
                  number: 'c.',
                  content:
                    'Temporary maintenance or partial service disruptions',
                },
              ],
            },
            {
              number: '5.2.3',
              content:
                'Refunds are not provided when partial functionality is limited but overall service access remains available.',
            },
          ],
        },
        {
          title: '5.3 Exceptions Eligible for Refund or Credit',
          items: [
            {
              number: '5.3.1',
              content:
                'Refunds or credits may be provided under the following circumstances:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Major Service Outage: Significant or prolonged service unavailability attributable to the Company.',
                },
                {
                  number: 'b.',
                  content:
                    'Duplicate or Erroneous Billing: Duplicate charges applied to the same account, or amounts charged in excess of the correct fee due to system errors.',
                },
                {
                  number: 'c.',
                  content:
                    'Other Company-Attributable Errors: Technical faults or Company errors that prevent normal service use. Partial of full refund of the monthly fee may apply.',
                },
              ],
            },
          ],
        },
        {
          title: '5.4 Subscription Cancellation',
          items: [
            {
              number: '5.4.1',
              content:
                'Users may cancel their subscription at any time, and no further fees will be charged from the next billing cycle following cancellation.',
            },
            {
              number: '5.4.2',
              content:
                'Fees already paid for the current billing period are non-refundable.',
            },
          ],
        },
        {
          title: '5.5 Fee Adjustments and Credit Issuance',
          items: [
            {
              number: '5.5.1',
              content:
                'The Company may issue adjustments or credits when the following billing errors are identified:',
              subItems: [
                {
                  number: 'a.',
                  content: 'Billing system errors',
                },
                {
                  number: 'b.',
                  content: 'Usage calculation errors (where applicable)',
                },
                {
                  number: 'c.',
                  content: 'Other technical errors affecting fee calculation',
                },
              ],
            },
            {
              number: '5.5.2',
              content:
                'Credits are automatically applied to upcoming billing cycles and are not redeemable for cash.',
            },
            {
              number: '5.5.3',
              content:
                'Credits remain valid for 12 months from the date of issuance and will expire thereafter.',
            },
          ],
        },
        {
          title: '5.6 Billing Dispute Resolution Procedure',
          items: [
            {
              number: '5.6.1',
              content:
                'Users may submit billing disputes within 30 days from the billing date.',
            },
            {
              number: '5.6.2',
              content:
                'The Company will review and respond within 10 business days, with extensions applied where additional documentation is reasonably required.',
            },
            {
              number: '5.6.3',
              content:
                'Both parties shall act in good faith to resolve disputes.',
            },
            {
              number: '5.6.4',
              content:
                'Any issues unresolved under this Chapter shall follow the dispute resolution procedures defined in the higher-level agreements (Customer Agreement and Integrated Terms of Service).',
            },
          ],
        },
        {
          title: '5.7 Miscellaneous',
          items: [
            {
              number: '5.7.1',
              content:
                'Refunds or adjustments will generally be processed using the same payment method originally used by the User.',
            },
            {
              number: '5.7.2',
              content:
                'Refund processing times may vary depending on the policies of card networks or payment gateways.',
            },
            {
              number: '5.7.3',
              content:
                'This refund and cancellation policy may be updated in accordance with applicable laws or Company internal policy changes.',
            },
          ],
        },
      ],
    },
    {
      title: '6. Disclaimer and Limitation of Liability',
      subsections: [
        {
          title: '6.1 Purpose and Scope',
          items: [
            {
              number: '6.1.1',
              content:
                'The purpose of this Chapter is to establish the disclaimer and liability standards applicable to the fees, payments, subscriptions, and refund policies of the Spacemap SAGA service. This is intended to prevent misunderstandings and disputes between the Company and the User.',
            },
            {
              number: '6.1.2',
              content:
                'This Chapter applies solely to matters directly related to the Pricing Policy. Broader liability standards governing the Service are defined in higher-level documents (such as the Customer Agreement and Integrated Terms of Service), and in case of any conflict, these higher-level documents shall prevail.',
            },
          ],
        },
        {
          title: '6.2 General Disclaimer',
          items: [
            {
              number: '6.2.1',
              content:
                'The Company uses commercially reasonable technical and operational efforts to provide the features included in each subscription plan.',
            },
            {
              number: '6.2.2',
              content:
                'The Company shall not be liable for damages arising from the following causes:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    "Issues related to the User's device, network environment, or browser configuration.",
                },
                {
                  number: 'b.',
                  content:
                    'Outages or policy changes by payment processors (e.g., card networks, PayPal).',
                },
                {
                  number: 'c.',
                  content:
                    'Force majeure events, including natural disasters, power failures, war, or communication disruptions.',
                },
                {
                  number: 'd.',
                  content:
                    'Violations of terms, policies, or usage requirements by the User.',
                },
                {
                  number: 'e.',
                  content:
                    'Differences in educational content caused by policy changes or delayed updates from external content providers.',
                },
              ],
            },
          ],
        },
        {
          title: '6.3 Limitation of Liability',
          items: [
            {
              number: '6.3.1',
              content:
                "For any claims related to fees or payments, the Company's total monetary liability shall not exceed the amount actually paid by the User for the corresponding month.",
            },
            {
              number: '6.3.2',
              content:
                'The Company shall not be liable for the following categories of damages:',
              subItems: [
                {
                  number: 'a.',
                  content:
                    'Indirect, special, or consequential damages, including loss of expected educational opportunities.',
                },
                {
                  number: 'b.',
                  content:
                    'Incidental damages, such as data loss or interruption of learning activities.',
                },
                {
                  number: 'c.',
                  content:
                    "Damages arising from the User's failure to comply with internal institutional approval or procurement processes.",
                },
              ],
            },
          ],
        },
        {
          title: '6.4 User Indemnification',
          items: [
            {
              number: '6.4.1',
              content:
                "If the User engages in certain actions that violate this Policy or higher-level agreements, the Company may impose service restrictions, suspend the User's account, terminate the contract, or claim damages. Any losses incurred shall be borne by the User:",
              subItems: [
                {
                  number: 'a.',
                  content:
                    "Providing false payment information or using another person's payment method without authorization",
                },
                {
                  number: 'b.',
                  content:
                    'Account sharing, unauthorized access, or any action intended to evade subscription fees',
                },
                {
                  number: 'c.',
                  content:
                    'Causing system disruption or service instability through abnormal usage',
                },
                {
                  number: 'd.',
                  content:
                    'Abusing SAGA policies, including fraudulent refund requests or unjustified billing disputes',
                },
              ],
            },
            {
              number: '6.4.2',
              content:
                'Actions performed by teachers, students, employees, partners, or any individuals designated by the User are deemed actions of the User for the purposes of this Chapter.',
            },
          ],
        },
        {
          title: '6.5 Relationship with Higher-Level Documents',
          items: [
            {
              number: '6.5.1',
              content:
                'This Chapter serves as a supplemental provision concerning liability and disclaimer standards specific to the Pricing Policy. The broader responsibility framework is governed by the following higher-level documents:',
              subItems: [
                {
                  number: 'a.',
                  content: 'Customer Agreement',
                },
                {
                  number: 'b.',
                  content: 'Integrated Terms of Service',
                },
              ],
            },
            {
              number: '6.5.2',
              content:
                'In case of any inconsistency between this Chapter and the higher-level documents, the higher-level documents shall control, and this policy shall apply only within the scope of the Pricing Policy.',
            },
          ],
        },
      ],
    },
    {
      title: '7. Governing Law and Dispute Resolution',
      subsections: [
        {
          title: '7.1 Purpose',
          items: [
            {
              number: '7.1.1',
              content:
                'The purpose of this Chapter is to establish the standards and procedures for handling objections and disputes related to fees, payments, subscriptions, and refund policies of the Spacemap SAGA service.',
            },
          ],
        },
        {
          title: '7.2 Governing Law',
          items: [
            {
              number: '7.2.1',
              content:
                'This Policy and all claims, payments, refunds, and dispute-related matters arising from it shall be governed by the laws of the Republic of Korea.',
            },
            {
              number: '7.2.2',
              content:
                'The laws of the Republic of Korea apply even when the User accesses the Service from abroad or uses overseas payment methods, unless otherwise agreed in writing. Taxation or foreign exchange regulations of each country may apply only to the extent necessary.',
            },
          ],
        },
        {
          title: '7.3 Objection and Review Procedure',
          items: [
            {
              number: '7.3.1',
              content:
                'Users may object to billing, refunds, or credit applications by submitting a written request via the support portal or designated email within 30 days from the billing date.',
            },
            {
              number: '7.3.2',
              content:
                'The Company will review the request and provide results to the User within 10 business days from the date of receipt. The review period may be adjusted if additional documents are required.',
            },
            {
              number: '7.3.3',
              content:
                'Both parties shall cooperate in good faith to resolve disputes. Unresolved matters are directed to dispute resolution procedures outlined in higher-level documents, specifically the Customer Agreement and Integrated Terms of Service.',
            },
          ],
        },
        {
          title: '7.4 Jurisdiction',
          items: [
            {
              number: '7.4.1',
              content:
                "For any legal action related to this Policy, the Seoul Central District Court, which has jurisdiction over the Company's principal office, shall be the court of exclusive first instance.",
            },
          ],
        },
      ],
    },
    {
      title: '8. Addendum',
      subsections: [
        {
          title: '8.1 Purpose',
          items: [
            {
              number: '8.1.1',
              content:
                'The purpose of this chapter is to clarify the final legal effect of the Policy by outlining provisions regarding the effective date, amendment procedures, document hierarchy, language rules, and severability of the Spacemap SAGA Pricing Policy.',
            },
          ],
        },
        {
          title: '8.2 Amendments to the Policy',
          items: [
            {
              number: '8.2.1',
              content:
                'The Company may amend the Policy when necessary due to changes in service structure, educational content, operating costs, or applicable laws and regulations.',
            },
            {
              number: '8.2.2',
              content:
                'If any amendment may adversely affect Users, the Company shall provide prior notice through the Spacemap portal or email at least 30 days before the effective date.',
            },
            {
              number: '8.2.3',
              content:
                'Minor revisions, such as corrections of typographical errors or wording adjustments that do not materially affect user rights or obligations, may take effect immediately and will be deemed notified through version history records.',
            },
          ],
        },
        {
          title: '8.3 Effective Date',
          items: [
            {
              number: '8.3.1',
              content:
                'The Policy becomes effective on the date specified in the document or on a date separately announced by the Company.',
            },
            {
              number: '8.3.2',
              content:
                'For existing Users, amended versions of the Policy shall apply from the first billing cycle following the effective date.',
            },
          ],
        },
        {
          title: '8.4 Hierarchy of Documents',
          items: [
            {
              number: '8.4.1',
              content:
                'If there is any inconsistency between this Policy and other documents, the following hierarchy shall apply:',
              subItems: [
                {
                  number: 'a.',
                  content: 'Customer Agreement',
                },
                {
                  number: 'b.',
                  content: 'Integrated Terms of Service',
                },
                {
                  number: 'c.',
                  content: 'This Pricing Policy (Annex C - SAGA)',
                },
                {
                  number: 'd.',
                  content:
                    'Other operating guidelines or informational documents',
                },
              ],
            },
            {
              number: '8.4.2',
              content:
                'This Policy serves as a detailed supplement regarding pricing-related matters and does not replace any higher-level agreement.',
            },
          ],
        },
        {
          title: '8.5 Language',
          items: [
            {
              number: '8.5.1',
              content:
                'This Policy is written in Korean. In the event of any inconsistency between the Korean version and its translations, the Korean version shall prevail.',
            },
          ],
        },
        {
          title: '8.6 Severability',
          items: [
            {
              number: '8.6.1',
              content:
                'If any provision of this Policy is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.',
            },
          ],
        },
      ],
    },
  ],
};
